import { useState, useEffect, useRef, useMemo } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, ArrowRight, CheckCircle, Copy, Mail, MessageSquare, QrCode, Lock, AlertTriangle, ChevronRight, X, Shield, Check, Search, User, MapPin, Star, UserCheck, Building, Send, Calendar, Clock, Video, Pill, FileText, PenTool, Phone, Share2, GraduationCap, Home, Navigation } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { useProfileStore } from '../../stores'
import { deriveAuthRequired } from '../../utils/authRequired'
import { casesAPI, doctorsAPI, providersAPI, agreementTemplateAPI } from '../../services/api'
import { nearestZip5 } from '../../utils/zip5Locate'
import { CURRENT_AGREEMENT_VERSION, CURRENT_HARDCODED_TEMPLATE, PLATFORM_AGREEMENT_TITLE, isPlatformAgreement } from '../../utils/agreementTemplate' // s101: the Orella Platform Agreement
import { readServerAppointmentRequest } from '../../utils/appointmentRequestOwner'
import { appointmentErrorKeyFor, toHHMM, bookableSlots } from '../../utils/bookableSlots'
import { buildClaimUrl, signupUrl } from '../../utils/claimUrl.js'
import { matchDoctorsToCase, getMatchingSpecialties, specialtyMatchesTarget } from '../../utils/doctorMatch'
import { specialtyChoicesFor, catalogMatchingSpecialties, doctorFitsChoice } from '../../utils/specialtyChoices' // s101 Build 67
import useQuestionnaireStore from '../../stores/useQuestionnaireStore'
import { specialtyChips } from '../../utils/caseReport'
import { useI18n } from '../../i18n'
import { formatProviderSpecialty } from '../../utils/specialtyResolver'
import { pendingStepCta } from '../../utils/shareStepCta'
import { apiErrorMessage } from '../../utils/apiErrorMessage'
import { getZipCoords, nearestZipFromCoords } from '../../utils/zipDistance'
import { doctorDistanceMiles } from '../../utils/locationDistance'
import DoctorsMap from '../doctors/DoctorsMap'
import {
  DoctorCard, DoctorLocationBar, DoctorFilterChips, DoctorViewToggle, DoctorFiltersSheet,
} from '../../components/doctors/DoctorSearchKit'
import { formatAddress } from '../../utils/formatAddress'
import { getInsuranceMatchType } from '../../utils/insuranceMatch'
import questionnairesData from '../../data/questionnaires.json'

// Same key the Doctors directory uses; the map and the toggle are gated on it.
const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_KEY || 'AIzaSyCJNsyeMsI4oYPOri_LV1s5JXurYpFzBu4'
import { ChatKeyframes, CompactHeader, OrellaChatBubble } from '../../components/OrellaChatHeader'
import ShareConversation from '../../components/ShareConversation'
import AgreementPreviewModal from '../../components/AgreementPreviewModal'
import AgreementChecklist from '../../components/AgreementChecklist' // s101 (D496)
import PracticeAgreementModal from '../../components/PracticeAgreementModal'
import { practiceAgreementAPI } from '../../services/api'
import { canAcceptSignature, practiceAgreementRecord, practiceSignKey, signatureStillValid } from '../../utils/practiceAgreement'
import BottomSheet from '../../components/BottomSheet'
import ConfirmModal from '../../components/ConfirmModal'
import { useBookingRecordingConsent } from '../../components/RecordingConsentSheet' // MEET PA0
import DirectoryNearby from '../../components/share/DirectoryNearby' // s99 D476
import MapPinCard from '../../components/share/MapPinCard' // s104 (TestFlight b70): the tapped pin's listing
import MapAreaButtons from '../../components/doctors/MapAreaButtons' // s106: shared with the Doctors tab
import { useMapArea } from '../../hooks/useMapArea' // s106
import { milesOrigin } from '../../utils/mapMyLocation' // PROD-1184 / PROD-1187
import ReferralNeeded from '../../components/referral/ReferralNeeded' // s106 referral screens A2
import { referralRequired, isOwnPcp, planLabel, defaultRequired, insPlanType } from '../../utils/referralRequired' // s106
import { pcpNeededFor } from '../../utils/pcpChoice' // s101 D497
import { dirMapPins } from '../../utils/dirMapPins' // s105: shared with the Doctors tab
import NetworkDisclaimer from '../../components/NetworkDisclaimer' // s105 (TestFlight b71)
import DoctorProfileScreen from '../doctors/DoctorProfileScreen'
import EligibilityBadge, { roleFromSpecialty } from '../../components/EligibilityBadge'
import { eligibilityAnswer } from '../../utils/eligibilityLimit'
import { useAgent } from '../../components/AgentContext'
import orellaOIcon from '../../assets/logos/orella_o_icon.png'
import { formatDate, formatDateTime, formatDateLong, formatAppointmentTime } from '../../utils/dateUtils'
import { formatSpecialtyName } from '../../utils/specialtyResolver'
import { canvasHasInk, signatureDataUrl } from '../../utils/signatureInk'
import { isSectionComplete, requiredCompletionPercent, missingBeforeShare } from '../../utils/profileCompletion'
import ContinueWithoutInsurance from '../../components/share/ContinueWithoutInsurance' // PROD-1132 Q1
import { caseBack } from '../../utils/caseBack' // PROD-654: back = the previous case screen
import BottomActionBar from '../../components/BottomActionBar' // PROD-1194 part C
import OfficePickerSheet from '../../components/share/OfficePickerSheet' // s110: which office gets the case
import PracticeSendSheet from '../../components/doctors/PracticeSendSheet' // s126 (TestFlight build 95): a count pin's office
import { groupOffices } from '../../utils/officeGroups' // s126: same as the Doctors tab (s123 board F)
import { otherOffices } from '../../utils/officeAddress' // s110
import { isOutsideAppointment } from '../../utils/outsideAppointment' // PROD-1160
import { clinicianName, clinicianTo } from '../../utils/clinicianName.js'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#46A8F9'

// v206.1: SPECIALTY_KEY_MAP + formatSpecialtyName moved to shared utils/specialtyResolver.js

// v242.75.0 — getMatchingSpecialties + its legacy fallback moved to
// utils/doctorMatch.js, alongside the matcher that consumes their output.

// Calculate expiration date (24 hours from now)
const getExpirationDate = () => {
  const date = new Date()
  date.setHours(date.getHours() + 24)
  return formatDateTime(date.toISOString())
}

// ★ s95: the before-share check reads THE owner (utils/profileCompletion.js). This was a
//   private copy that trusted identification.completed, which the owner does not (it
//   requires all eight fields). Sign-up now defers insurance / allergies / medications,
//   so this check is what keeps a case from being sent with them missing.
function calculateProfileStatus(profile) {
  return {
    completionPercent: requiredCompletionPercent(profile),
    allergiesComplete: isSectionComplete('allergies', profile),
    medicationsComplete: isSectionComplete('medications', profile),
    identificationComplete: isSectionComplete('identification', profile),
    insuranceComplete: isSectionComplete('insurance', profile),
  }
}

export default function ShareScreen() {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentCase, updateCase, updateAndSaveCase, cases, setCurrentCase } = useCaseStore()
  // PROD-654: header back steps over the Share choice once the case has picked its side (that
  //   screen would forward straight back here, utils/caseBack.js).
  const shareBackSkip = (p) => p === '/case/share-choice' && !!currentCase?.doctorSource
  const profile = useProfileStore((state) => state.profile)
  const { t, hasText } = useI18n()
  const appointmentErrorKey = (e) => appointmentErrorKeyFor(e, hasText) // PROD-1089: es withholds the physician_inactive sentence
  // MEET PA0: the booking-time recording question of a video visit (RecordingConsentSheet.jsx).
  const { askRecordingConsent, consentSheet } = useBookingRecordingConsent()

  // Steps: 'find-doctor', 'confirm-doctor', 'doctor-sent', 'initial' (code), 'generated' (code)
  const [step, setStep] = useState('find-doctor')
  const [shareCode, setShareCode] = useState('')
  const [pin, setPin] = useState('')
  const [expirationDate, setExpirationDate] = useState('')
  const [copied, setCopied] = useState(false)
  const [showSkipWarning, setShowSkipWarning] = useState(false)
  const [profileSkipped, setProfileSkipped] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [error, setError] = useState('')
  const [submitPhase, setSubmitPhase] = useState(null) // null | 'sending' | 'success' | 'exit'
  const [showContent, setShowContent] = useState(false)

  // Suppress FAB during submit overlay
  const { suppressFab } = useAgent()
  useEffect(() => {
    if (submitPhase) { suppressFab?.(true); return () => suppressFab?.(false) }
  }, [!!submitPhase])
  
  // Doctor search state
  const [doctors, setDoctors] = useState([])
  const [filteredDoctors, setFilteredDoctors] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  // ★★ v242.103.0 — THE PICKER NOW USES THE DIRECTORY'S FILTER MODEL, not a Set of
  //    three booleans. Dr. Rami: the picker's search adopts the Doctors screen's.
  //    `nearby` was a boolean pinned at 25 mi; the directory's Distance chip opens a
  //    radius picker, so it becomes a NUMBER (null = any) and the old chip's meaning
  //    survives exactly as distanceFilter === 25.
  //    ★ There is no specialtyFilter: the CASE fixes the specialty here, which is
  //      why that chip renders LOCKED instead of opening a picker.
  const [distanceFilter, setDistanceFilter] = useState(null)
  // ★ s101 (Build 67, Dr. Rami): the specialty is a CHOICE - the case area's admin-assigned
  //   specialties + Primary care, always (utils/specialtyChoices). null = the first (the case's own).
  //   `showFilter`: 'all' | 'orella' | 'other' - "Doctors on Orella vs outside Orella".
  const [specialtyChoiceId, setSpecialtyChoiceId] = useState(null)
  const [showFilter, setShowFilter] = useState('all')
  const [insuranceActive, setInsuranceActive] = useState(false)
  const [virtualActive, setVirtualActive] = useState(false)
  const [sortBy, setSortBy] = useState('soonest')   // 'soonest' | 'distance' | 'rating'
  const [showFilters, setShowFilters] = useState(false)
  const [viewMode, setViewMode] = useState('map')   // map-first, as the directory is
  const [selectedMapDoc, setSelectedMapDoc] = useState(null)
  // ★ s101 Phase 4: the directory half of the merged search (DirectoryNearby variant 'section')
  //   reports its rows here so the one map draws them beside the Orella pins.
  const [dirRows, setDirRows] = useState([])
  const [dirLoading, setDirLoading] = useState(false) // s114 B2: the directory search is in flight (the map's spinner)
  const [selectedDirNpi, setSelectedDirNpi] = useState(null)
  // s110 (mockup screen 3): a directory doctor with several offices -> "Which office should get your case?"
  const [officePick, setOfficePick] = useState(null)
  const [practiceSheet, setPracticeSheet] = useState(null) // s126: a pin for several clinicians at one address
  const autoLocationTried = useRef(false)
  // ★ s101 Phase 4 "Search this area": `areaView` is where the patient has moved the map (offered
  //   as a button); `area` is the centre being searched once they tap it ({ lat, lng, zip }).
  //   With an area, both lists are measured from it; "My location" goes back. ★ s120 (Dr. Rami): the
  //   distance CHIP owns the radius (25 mi when unset); "Search this area" only moves the centre.
  //   ★ s106 (TestFlight b72, "They need to match"): the state and the two buttons live in
  //   hooks/useMapArea + components/doctors/MapAreaButtons, shared with the Doctors tab.
  const mapArea = useMapArea()
  const { setAreaView, area } = mapArea
  const resetFilters = () => {
    setDistanceFilter(null); setVirtualActive(false); setInsuranceActive(false); setSortBy('soonest')
    setSpecialtyChoiceId(null); setShowFilter('all')
  }
  const activeFilterCount = (distanceFilter ? 1 : 0) + (virtualActive ? 1 : 0) +
    (insuranceActive ? 1 : 0) + (sortBy !== 'soonest' ? 1 : 0) + (showFilter !== 'all' ? 1 : 0)
  // The choices, from the LIVE catalog (admin edits) first, the bundled one as fallback.
  const liveSpecialties = useQuestionnaireStore((st) => st.specialties)
  const specialtyChoices = useMemo(() => {
    const listed = catalogMatchingSpecialties(liveSpecialties, currentCase?.specialtyId)
      || getMatchingSpecialties(currentCase?.specialtyId, currentCase?.focusAreaId)
    const area = specialtyChips(currentCase).find((c) => c.id === 'area')
    return specialtyChoicesFor(listed, area ? area.prefixes : [])
  }, [liveSpecialties, currentCase?.specialtyId, currentCase?.focusAreaId]) // eslint-disable-line react-hooks/exhaustive-deps
  const activeChoice = specialtyChoices.find((c) => c.id === specialtyChoiceId) || specialtyChoices[0]

  // v213.4 (C-A-181): Active location source — drives the "nearby" filter and
  // distance display. Defaults to 'home' (profile zipCode) but the patient can
  // tap to switch to 'current' (browser geolocation, reverse-geocoded to ZIP3)
  // or 'manual' (typed ZIP). This fixes the bug where a patient traveling in
  // Houston was shown LA doctors because their profile zip was in LA.
  const homeZip = profile?.identification?.zipCode || ''
  const [locationSource, setLocationSource] = useState('home') // 'home' | 'current' | 'manual'
  const [currentLocationZip, setCurrentLocationZip] = useState('')
  // PROD-655: the device fix itself (on-device only), as the Doctors tab keeps it.
  const [currentLocationCoords, setCurrentLocationCoords] = useState(null)
  // NIT-3 (PROD-655 repair): the label is translated at RENDER, never stored, so it follows a
  //   later language switch (a stored t() result stayed in the language of the moment of the fix).
  const currentLocationLabel = currentLocationCoords ? t('doctors.currentLocation', { defaultValue: 'Current location' }) : ''
  const [manualZip, setManualZip] = useState('')
  const [locationLoading, setLocationLoading] = useState(false)
  const [locationError, setLocationError] = useState('')
  const [showLocationSheet, setShowLocationSheet] = useState(false)

  // The ZIP currently used for nearby filtering / distance display.
  const activeZip = locationSource === 'current'
    ? currentLocationZip
    : (locationSource === 'manual' ? manualZip : homeZip)

  // ★★ PROD-655 (patient P2, released by Alex 2026-10-06 ~17:23 PDT): "Use my current location"
  //   keeps the REAL device position, as the Doctors tab does (DoctorsScreen useDeviceLocation):
  //   the map centres on it with the blue dot (centerPrecise) and the distances, the sort and the
  //   "Within N mi" filter are on-device haversine miles to each practice (doctorDistanceMiles).
  //   It used to be reduced to a ZIP3 area ("~910xx") whose centroid drove the map and the
  //   distances. Google sees only the map viewport, as on the Doctors tab; the fix itself never
  //   leaves the device. The home ZIP and a typed ZIP are unchanged (ZIP centroid).
  const currentCoords = locationSource === 'current' ? currentLocationCoords : null
  const distanceOf = (d) => doctorDistanceMiles(d, { coords: currentCoords, zip: activeZip })

  // v242.103.0 — the map centres on the ZIP the list is already filtered by, so the
  // pins and the rows can never disagree about where "near you" is.
  // ★ A ZIP centre is coarse (DoctorsMap draws a faint area circle); the device fix (PROD-655)
  //   is precise (the blue dot).
  const mapCenter = (() => {
    if (currentCoords && Number.isFinite(currentCoords.lat) && Number.isFinite(currentCoords.lng)) {
      return { lat: currentCoords.lat, lng: currentCoords.lng, label: currentLocationLabel, precise: true }
    }
    // ★★ getZipCoords returns AN ARRAY [lat, lng], not {lat, lng}. Reading `.lat`
    //    off it gave undefined, and Google Maps threw
    //    "not a LatLng or LatLngLiteral: in property lat: not a number" — which
    //    took down the whole screen the moment the map mounted. DoctorsScreen has
    //    always indexed it correctly; I assumed the shape instead of reading it.
    // ★  The isFinite guard is the interlock: a ZIP3 miss returns null and an
    //    unknown prefix must degrade to a list, never to a thrown map.
    const c = activeZip ? getZipCoords(activeZip) : null
    if (!Array.isArray(c) || !Number.isFinite(c[0]) || !Number.isFinite(c[1])) return null
    return { lat: c[0], lng: c[1], label: activeZip, precise: false }
  })()

  const requestCurrentLocation = async ({ silent = false } = {}) => {
    setLocationError('')
    if (!navigator.geolocation) {
      setLocationError(t('share.geolocationNotSupported', 'Your browser does not support location services.'))
      return
    }
    setLocationLoading(true)
    try {
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 5 * 60 * 1000
        })
      })
      const { latitude, longitude } = pos.coords
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        setLocationError(t('share.locationLookupFailed', "Couldn't determine your area from your location."))
        setLocationSource('home')
      } else {
        // PROD-655: the fix drives the map and the distances; the nearest ZIP stays only as the
        //   fallback for a practice without geocoded coordinates.
        // ★ s101 Phase 4 (x_24): the nearest 5-DIGIT ZIP, picked on the phone from its areas'
        //   centroids (utils/zip5Locate.js); "<ZIP3>00" put a Hollywood patient in Whittier.
        const nearest = nearestZipFromCoords(latitude, longitude)
        setCurrentLocationCoords({ lat: latitude, lng: longitude })
        setCurrentLocationZip(nearest ? nearest.zip : '')
        setLocationSource('current')
        const z5 = await nearestZip5(latitude, longitude, providersAPI.zipAreas)
        if (z5 && z5.zip) setCurrentLocationZip(z5.zip)
      }
    } catch (err) {
      console.warn('[ShareScreen] geolocation error:', err && err.code)
      const code = err && err.code
      if (silent) {
        // the automatic attempt on opening (x_25): no message, the home ZIP stays
      } else if (code === 1) {
        setLocationError(t('share.locationDenied', 'Location permission denied. Showing doctors near your home address.'))
      } else {
        setLocationError(t('share.locationFailed', "Couldn't get your current location. Showing doctors near your home address."))
      }
      setLocationSource('home')
    } finally {
      setLocationLoading(false)
      // v225.10.0 (#5): Close the sheet once the location attempt completes.
      // Previously only the home/manual taps closed it; the current-location
      // path left the sheet open, so the updated chip/results stayed hidden
      // behind the modal and only "showed up on the next screen".
      setShowLocationSheet(false)
    }
  }
  
  // ★ s101 Phase 4 (x_25, TestFlight b65: "default should be current location"): the doctor
  //   search opens on where the patient IS. One automatic attempt the first time the list shows;
  //   a refusal or a failure is silent and the home ZIP stays, as before.
  useEffect(() => {
    if (step !== 'find-doctor' || autoLocationTried.current || locationSource !== 'home') return
    autoLocationTried.current = true
    requestCurrentLocation({ silent: true })
  }, [step]) // eslint-disable-line react-hooks/exhaustive-deps

  // v194.1: Default to insurance filter when patient has insurance data
  const patientHasInsurance = !!(profile?.insurance?.provider || profile?.insurance?.planName) && !profile?.insurance?.selfPay
  const defaultFiltersApplied = useRef(false)
  useEffect(() => {
    if (patientHasInsurance && !defaultFiltersApplied.current) {
      setInsuranceActive(true)
      defaultFiltersApplied.current = true
    }
  }, [patientHasInsurance])
  const [selectedDoctor, setSelectedDoctor] = useState(null)
  // ★ s106 (canvas "Orella Referrals Screens" A2, decisions 1-2): a specialist whose plan rules need a
  //   referral (utils/referralRequired, the API's _referral_required) asks first: see my PCP, ask my PCP
  //   for a referral ('ask'), or upload one ('upload'). Those two send the case WITHOUT booking (booking
  //   opens once the referral is on file) and then open the request / upload screen.
  const [referralChoice, setReferralChoice] = useState(null)
  useEffect(() => { setReferralChoice(null) }, [selectedDoctor?.id])
  const myPcp = profile?.insurance?.pcp || null
  const needsReferral = (doc) => !!doc && !isOwnPcp(doc, myPcp) && referralRequired(doc.accepted_insurance, profile?.insurance || {})
  const referralHold = referralChoice === 'ask' || referralChoice === 'upload'
  const referralPlan = planLabel(profile?.insurance || {})
  const pcpFirst = !!myPcp && (pcpNeededFor(profile?.insurance?.coverageType) || defaultRequired(insPlanType(profile?.insurance?.planType)))
  // A2 option 1 and the list's PCP card: the PCP on Orella is chosen like any doctor (no referral is needed
  //   to see your own PCP); one outside Orella gets the case by fax through the share hub.
  const choosePcp = async () => {
    if (!myPcp) { navigate('/profile/insurance'); return }
    if (myPcp.doctorId) {
      let doc = (doctors || []).find((d) => String(d.id) === String(myPcp.doctorId)) || null
      if (!doc) { try { doc = await doctorsAPI.get(myPcp.doctorId) } catch (_) { doc = null } }
      if (doc && doc.id) { setSelectedDoctor(doc); setStep('confirm-doctor'); return }
    }
    const provider = { npi: myPcp.npi || '', name: myPcp.name, credential: myPcp.credential || null, specialty: myPcp.specialty || null,
      phone: myPcp.phone || null, fax: myPcp.fax || null, faxOnFile: !!myPcp.fax, faxSource: myPcp.fax ? 'profile' : null,
      address: myPcp.address ? { line1: myPcp.address } : null }
    navigate('/case/share-hub', { state: { next: { to: '/case/send-report', state: { provider } } } })
  }
  // P1 round 4 (L2): the 'orella-preferred-doctor' handoff named a physician without real hours
  // P1 round 5 (M4): complete_onboarding saved the case but not the appointment
  //   (appointmentCreated:false for a request that carried a slot). Shown in place of the
  //   conversation, so its Send cannot run complete_onboarding a second time.
  const [onboardAppointmentNotSaved, setOnboardAppointmentNotSaved] = useState(false)
  const [loadingDoctors, setLoadingDoctors] = useState(false)
  const [sendingToDoctor, setSendingToDoctor] = useState(false)
  const [showDoctorProfile, setShowDoctorProfile] = useState(null) // doctor object or null
  // v214.9.3: When set, the full DoctorProfileScreen renders as a bottom sheet
  // instead of navigating away. Holds { doctorId, mode } so the sheet knows the
  // context (typically mode='select' from the doctor list).
  const [fullProfileSheet, setFullProfileSheet] = useState(null)
  // TEST 3.15 (patient push 3a): the send conversation's current step, as it reports it
  const [conversationStep, setConversationStep] = useState(null)

  // v213.4 (C-A-198): Watchdog — if sendingToDoctor stays true longer than ~95s,
  // something got stuck silently (network hang past API timeout, lost state, etc).
  // Surface a clear error and let the patient retry instead of leaving them on a
  // spinning button forever.
  useEffect(() => {
    if (!sendingToDoctor) return
    const watchdog = setTimeout(() => {
      console.warn('[ShareScreen] sendingToDoctor watchdog fired — clearing stuck state')
      setSendingToDoctor(false)
      setSubmitPhase(null)
      setError(t('errors2.sendingTimedOut', "Sending is taking longer than usual. Please check your connection and try again."))
    }, 95000)
    return () => clearTimeout(watchdog)
  }, [sendingToDoctor])

  // v222.0.0 Stedi (Seam 1): silent eligibility check when entering confirm-doctor
  // with a selected doctor on a real (synced) case. Cache short-circuits server-side.
  const [eligibilityChecking, setEligibilityChecking] = useState(false)
  const [eligibility, setEligibility] = useState(profile?.insurance?.last_eligibility || null)
  // v235.1.0 — derived auth-required routing signal passed into ShareConversation.
  const authRequired = deriveAuthRequired(profile?.insurance, eligibility)

  useEffect(() => {
    // Sync the badge state with the profile store whenever insurance changes.
    // ★ s105 (TestFlight b71, Dr. Rami: "is this running eligibility against the doctor being
    //   requested?"): on the confirm step the badge shows ONLY a result for the doctor being booked
    //   (forDoctorNpi). A stored check for another NPI (often the Orella practice NPI from onboarding)
    //   is not shown as this doctor's coverage; the check below runs for this doctor instead.
    const last = profile?.insurance?.last_eligibility || null
    // (a const, not an `if (step === 'confirm-doctor' …)`: check-no-invented-slots R25 finds the confirm
    //  step's render block as the first such if.)
    const onConfirmStep = step === 'confirm-doctor'
    if (onConfirmStep && selectedDoctor?.npi && last && last.forDoctorNpi && last.forDoctorNpi !== selectedDoctor.npi) {
      setEligibility(null)
      return
    }
    setEligibility(last)
  }, [profile?.insurance?.last_eligibility, step, selectedDoctor?.npi])

  useEffect(() => {
    // Trigger only on confirm-doctor with a real selected doctor + synced case
    if (step !== 'confirm-doctor') return
    if (!selectedDoctor?.id) return
    if (!currentCase?.id || String(currentCase.id).startsWith('local-')) return
    // Skip if patient is self-pay (nothing to verify)
    const ins = profile?.insurance || {}
    if (ins.selfPay || ins.coverageType === 'self-pay' || ins.coverageType === 'hsa') return
    if (!ins.memberId) return  // No insurance on file yet — silent skip
    // Skip if a fresh check (<1h) already exists for this doctor's NPI
    const last = ins.last_eligibility
    if (last?.checkedAt && last?.forDoctorNpi === selectedDoctor.npi) {
      try {
        const ageMs = Date.now() - new Date(last.checkedAt).getTime()
        if (ageMs < 60 * 60 * 1000) {
          setEligibility(last)
          return
        }
      } catch {}
    }

    let cancelled = false
    setEligibilityChecking(true)
    // s105: name the doctor being booked; the case may not name one yet (it is sent after this step).
    casesAPI.checkEligibility(currentCase.id, { skipCache: false, doctorId: selectedDoctor.id })
      .then((resp) => {
        if (cancelled) return
        // ★ PROD-689 client half (P4-4): through the one reader; a limit answer's stored result
        //   is shown as it is, its null leaves the badge as it was.
        // ★ PROD-936 PAT-2: a reuse answer (reused: true, this coverage and this doctor, under 30 days
        //   old, no payer call) is that stored result too; the compact badge shows its age (showAge).
        const answer = eligibilityAnswer(resp)
        if (answer.eligibility) {
          setEligibility(answer.eligibility)
          // Also persist into the profile store so other screens see it
          const updateProfile = useProfileStore.getState().updateProfile
          const currentIns = useProfileStore.getState().profile?.insurance || {}
          if (updateProfile) {
            updateProfile({ insurance: { ...currentIns, last_eligibility: answer.eligibility } })
          }
        }
      })
      .catch((e) => {
        if (!cancelled) console.warn('[ELIGIBILITY] confirm-doctor check failed:', e?.message || e)
      })
      .finally(() => {
        if (!cancelled) setEligibilityChecking(false)
      })

    return () => { cancelled = true }
  }, [step, selectedDoctor?.id, currentCase?.id])
  
  const [showAgreementPreview, setShowAgreementPreview] = useState(false)
  
  // Appointment request state
  const [requestAppointment, setRequestAppointment] = useState(true)
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [appointmentType, setAppointmentType] = useState('video') // video, in-person
  
  // Doctor availability state
  const [availableSlots, setAvailableSlots] = useState(null)
  // D7 (PD7-1): where each slot is (utils/bookableSlots.js slotPlacesOf); null for an API
  //   without `locations`, and then ShareConversation shows exactly what it did before D7.
  const [slotPlaces, setSlotPlaces] = useState(null)
  const [loadingSlots, setLoadingSlots] = useState(false)
  // D-g (round 2, A2): WHY there is nothing to pick (utils/bookableSlots.js reasons), and a
  // counter the "Try again" on a failed read bumps. Replaces doctorAcceptsAppointments,
  // which was set and never read.
  const [slotsReason, setSlotsReason] = useState(null)
  const [slotsAttempt, setSlotsAttempt] = useState(0)
  // P1 round 6 (A1): bumped when a send comes back as a slot conflict. ShareConversation
  // owns the picker, so this is the signal that sends it back to its time step (the date
  // kept, the taken time cleared) while the open times are read again (slotsAttempt).
  const [slotConflictSeq, setSlotConflictSeq] = useState(0)
  
  // Cancel share state
  const [cancellingShare, setCancellingShare] = useState(false)
  // PROD-285: "Revoke Access" asks first, and keeps the share when the server refuses.
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false)
  const [revokingAccess, setRevokingAccess] = useState(false)
  
  // Pharmacy selection state
  const [selectedPharmacy, setSelectedPharmacy] = useState(null)
  const [pharmacySearchQuery, setPharmacySearchQuery] = useState('')
  const [pharmacyResults, setPharmacyResults] = useState([])
  const [searchingPharmacy, setSearchingPharmacy] = useState(false)
  
  // Patient agreement state
  const [agreementSigned, setAgreementSigned] = useState(false)
  const [showAgreementModal, setShowAgreementModal] = useState(false)
  const [signature, setSignature] = useState(null)
  // v242.64.0 (BUG-5): whether the canvas actually carries ink. See
  // src/utils/signatureInk.js — this used to be assumed, and a blank
  // canvas was being accepted as a signed care agreement.
  const [hasInk, setHasInk] = useState(false)
  const signatureCanvasRef = useRef(null)
  // ★ s101 (D496): the chosen physician's OWN agreement (null = they have none / are not asking).
  //   One signature covers it and Orella's; `signedPracticeKey` is the agreement that signature was
  //   given for, so switching to a doctor with a different agreement asks for a new signature.
  const [practiceTemplate, setPracticeTemplate] = useState(null)
  const [agreeOrella, setAgreeOrella] = useState(false)
  const [agreePractice, setAgreePractice] = useState(false)
  const [signedPracticeKey, setSignedPracticeKey] = useState('none')
  const [showPracticeView, setShowPracticeView] = useState(false)
  // v225.25.0 (Item 3): scroll container for the doctor-sent confirmation column.
  const doctorSentScrollRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)

  // ★★★ s101 (Dr. Rami 2026-10-13): EVERY new signature is of the Orella Platform Agreement. ITS
  //   EDITABLE COPY IS THE ADMIN PANEL'S GLOBAL TEMPLATE (Compliance › Patient agreement): the active
  //   global template is fetched WITHOUT a case/doctor scope (a practice override is the practice's
  //   own document now) and bound + snapshotted when its title names the Platform Agreement. Until
  //   one is published - or if the fetch fails - the default text is signed (marker 'v4.0').
  //   Records signed before keep rendering the version they carry.
  const [platformTemplate, setPlatformTemplate] = useState(null)
  useEffect(() => {
    let cancelled = false
    agreementTemplateAPI.active()
      .then((tpl) => { if (!cancelled && isPlatformAgreement(tpl) && Array.isArray(tpl.sections) && tpl.sections.length > 0) setPlatformTemplate(tpl) })
      .catch((err) => console.warn('[ShareScreen] platform agreement fetch failed:', err))
    return () => { cancelled = true }
  }, [])
  const buildPatientAgreement = (sig) => {
    const base = { signed: true, signedAt: new Date().toISOString(), signature: sig, method: 'app' }
    // s101 (D496): the physician's own agreement, when the signature was given for THIS one.
    const practice = practiceTemplate && signedPracticeKey === practiceSignKey(practiceTemplate)
      ? practiceAgreementRecord(practiceTemplate, sig, base.signedAt) : null
    const withPractice = (r) => (practice ? { ...r, practiceAgreement: practice } : r)
    if (platformTemplate) {
      return withPractice({ ...base, agreementVersion: platformTemplate.version, title: platformTemplate.title,
        sections: platformTemplate.sections, effectiveDate: platformTemplate.effectiveDate })
    }
    return withPractice({ ...base, agreementVersion: CURRENT_AGREEMENT_VERSION, hardcodedTemplate: CURRENT_HARDCODED_TEMPLATE, title: PLATFORM_AGREEMENT_TITLE })
  }

  // s101 (D496): load the chosen Orella physician's own agreement; a signature given for another
  //   agreement (or for none) no longer counts - the patient signs again.
  useEffect(() => {
    let alive = true
    const id = selectedDoctor && selectedDoctor.id
    if (!id) { setPracticeTemplate(null); return undefined }
    practiceAgreementAPI.forDoctor(id)
      .then((a) => {
        if (!alive) return
        setPracticeTemplate(a || null)
        setAgreePractice(false)
        if (agreementSigned && !signatureStillValid(signedPracticeKey, a || null)) setAgreementSigned(false)
      })
      .catch(() => { if (alive) setPracticeTemplate(null) })
    return () => { alive = false }
  }, [selectedDoctor?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // State for tracking case loading
  const incomingCaseId = location.state?.caseId
  const isMDOnboard = location.state?.mdOnboard === true // MD-initiated case onboarding mode
  const [isInitialized, setIsInitialized] = useState(false)
  const [loadAttempts, setLoadAttempts] = useState(0)
  const maxAttempts = 50
  const [mdOnboardMode, setMDOnboardMode] = useState(false) // Track MD onboard mode locally
  const [mdOnboardAppointment, setMDOnboardAppointment] = useState(null) // v191.8: Pre-existing appointment from MD
  
  // Restore form state from sessionStorage (for when returning from profile)
  useEffect(() => {
    const savedFormState = sessionStorage.getItem('shareScreenFormState')
    if (savedFormState && incomingCaseId) {
      try {
        const formState = JSON.parse(savedFormState)
        // Only restore if same case
        if (formState.caseId === incomingCaseId) {
          console.log('[ShareScreen] Restoring form state from sessionStorage')
          if (formState.selectedPharmacy) setSelectedPharmacy(formState.selectedPharmacy)
          if (formState.agreementSigned) setAgreementSigned(formState.agreementSigned)
          if (formState.signature) setSignature(formState.signature)
          if (formState.signedPracticeKey) setSignedPracticeKey(formState.signedPracticeKey)
          if (formState.selectedDate) setSelectedDate(formState.selectedDate)
          if (formState.selectedTime) setSelectedTime(formState.selectedTime)
          if (formState.appointmentType) setAppointmentType(formState.appointmentType)
          if (typeof formState.requestAppointment === 'boolean') setRequestAppointment(formState.requestAppointment)
        }
        // Clear after restoring
        sessionStorage.removeItem('shareScreenFormState')
      } catch (e) {
        console.error('[ShareScreen] Failed to parse saved form state:', e)
        sessionStorage.removeItem('shareScreenFormState')
      }
    }
  }, [incomingCaseId])

  // ★★ v242.207.0 — DiagnoseScreen's share-code doctor panel hands a doctor over
  //   WITHOUT revoking the share code (Alex, 2026-09-23: the code dies only when
  //   the send succeeds). The case therefore still carries `shareCode`, and the
  //   existing-share effect below would otherwise throw the patient onto the
  //   'generated' code screen instead of the doctor's slot picker. Captured at
  //   mount, because the handoff effect clears history state.
  const doctorHandoffRef = useRef(!!location.state?.selectedDoctor?.id)
  // ★ D469 (Dr. Rami 2026-10-10): the share hub's "Need a Doctor?" opens the Orella doctor list.
  //   The hub has already minted the share code, and a case with a code otherwise lands on the
  //   'generated' (share code) step below, never on the list he asked for.
  const findDoctorRef = useRef(location.state?.findDoctor === true)

  // v151.2.0: Handle doctor selection returning from DoctorProfileScreen
  useEffect(() => {
    const returnedDoctor = location.state?.selectedDoctor
    if (returnedDoctor && returnedDoctor.id) {
      setSelectedDoctor(returnedDoctor)
      setStep('confirm-doctor')
      // Clear the state so it doesn't re-trigger
      window.history.replaceState({}, '')
    }
  }, [location.state?.selectedDoctor])
  
  // v190.2 BT-35: Auto-select preferred doctor from doctor profile "Start a case" flow
  useEffect(() => {
    // Only run on initial mount when no doctor already selected and on the find-doctor step
    if (selectedDoctor || step !== 'find-doctor') return
    try {
      const stored = sessionStorage.getItem('orella-preferred-doctor')
      if (stored) {
        const preferredDoc = JSON.parse(stored)
        // ★★ P1 round 4 (L2; Alex D-g): the handoff is a picker that leads to a send, so it
        //   follows the share picker's own rule. A physician without real hours (the
        //   profile's `bookable` from /full-profile, carried by StartCaseSheet) is NOT
        //   auto-selected: the handoff is consumed, the patient stays on the doctor list
        //   (which is hasRealHours-filtered) and reads "online booking unavailable". Its own
        //   state, not `error`: loadDoctors clears `error` in the same mount.
        // ★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116): REVERSED. A physician without real hours is
        //   selected like any other; the date-time step then asks the patient to REQUEST a time.
        if (preferredDoc?.id) {
          console.log('[ShareScreen] Auto-selecting preferred doctor')
          setSelectedDoctor(preferredDoc)
          setStep('confirm-doctor')
          sessionStorage.removeItem('orella-preferred-doctor')
        }
      }
    } catch {}
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  // v145.3.0: Pre-populate pharmacy from profile if not already set
  useEffect(() => {
    if (!selectedPharmacy && profile?.preferredPharmacy) {
      setSelectedPharmacy(profile.preferredPharmacy)
    }
  }, [profile?.preferredPharmacy])
  
  // Save form state to sessionStorage before navigating to profile
  const saveFormStateToSession = () => {
    const formState = {
      caseId: currentCase?.id || incomingCaseId,
      selectedPharmacy,
      agreementSigned,
      signature,
      signedPracticeKey,
      selectedDate,
      selectedTime,
      appointmentType,
      requestAppointment
    }
    console.log('[ShareScreen] Saving form state to sessionStorage')
    sessionStorage.setItem('shareScreenFormState', JSON.stringify(formState))
  }
  
  // Handle case loading
  useEffect(() => {
    // If we already have currentCase that matches, we're good
    if (currentCase && (!incomingCaseId || currentCase.id === incomingCaseId)) {
      setIsInitialized(true)
      return
    }
    
    if (incomingCaseId) {
      // First check if currentCase already has this ID
      if (currentCase?.id === incomingCaseId) {
        setIsInitialized(true)
        return
      }
      
      // Try to find in cases array
      const targetCase = cases.find(c => c.id === incomingCaseId)
      if (targetCase) {
        setCurrentCase(targetCase)
        setIsInitialized(true)
        return
      }
      
      // Wait a bit for cases to load
      if (loadAttempts < maxAttempts) {
        const timer = setTimeout(() => setLoadAttempts(prev => prev + 1), 100)
        return () => clearTimeout(timer)
      } else {
        // After max attempts, check currentCase one more time 
        // (it might have been set during async ops)
        if (currentCase) {
          setIsInitialized(true)
        } else {
          console.warn('[ShareScreen] Could not load case:', incomingCaseId)
          setIsInitialized(true)
        }
      }
    } else if (!currentCase) {
      // No incoming case ID and no current case - wait briefly
      if (loadAttempts < 10) {
        const timer = setTimeout(() => setLoadAttempts(prev => prev + 1), 100)
        return () => clearTimeout(timer)
      } else {
        setIsInitialized(true)
      }
    } else {
      setIsInitialized(true)
    }
  }, [incomingCaseId, currentCase, cases, setCurrentCase, loadAttempts])

  // Check if there's an existing share code OR already sent to doctor
  useEffect(() => {
    if (currentCase?.sentToDoctor) {
      // Already sent to an Orella doctor - set the doctor from case data
      const doctor = currentCase.doctor
      if (doctor) {
        const doctorObj = {
          id: doctor.id,
          first_name: doctor.first_name || doctor.firstName,
          last_name: doctor.last_name || doctor.lastName,
          specialty: ((doctor.specialties && doctor.specialties[0]) || ''),
          practice_name: doctor.practice_name || doctor.practiceName,
          practice_address: doctor.practice_address,
          practice_city: doctor.practice_city,
          practice_state: doctor.practice_state,
          practice_zip: doctor.practice_zip,
          phone: doctor.phone || doctor.practice_phone,
          practice_phone: doctor.practice_phone,
          board_certified: doctor.board_certified,
          profile_photo: doctor.profile_photo,
          bio: doctor.bio,
          education: doctor.education,
          languages: doctor.languages,
          npi: doctor.npi
        }
        console.log('[ShareScreen] Already sent case - Setting doctor')
        setSelectedDoctor(doctorObj)
      }
      setStep('doctor-sent')
    } else if (currentCase?.originatorType === 'md_referral' && currentCase?.doctor && !currentCase?.sentToDoctor) {
      // v211.7: Referral-spawned case — doctor_id is pre-set by referring MD
      // Pre-select the receiving MD so patient skips doctor search
      const doctor = currentCase.doctor
      const doctorObj = {
        id: doctor.id,
        first_name: doctor.first_name || doctor.firstName,
        last_name: doctor.last_name || doctor.lastName,
        specialty: ((doctor.specialties && doctor.specialties[0]) || ''),
        practice_name: doctor.practice_name || doctor.practiceName,
        practice_address: doctor.practice_address,
        practice_city: doctor.practice_city,
        practice_state: doctor.practice_state,
        practice_zip: doctor.practice_zip,
        phone: doctor.phone || doctor.practice_phone,
        practice_phone: doctor.practice_phone,
        board_certified: doctor.board_certified,
        profile_photo: doctor.profile_photo,
        bio: doctor.bio,
        education: doctor.education,
        languages: doctor.languages,
        npi: doctor.npi
      }
      console.log('[ShareScreen] Referral case — pre-selecting referred doctor')
      setSelectedDoctor(doctorObj)
      // Don't auto-advance to 'doctor-sent' — let patient confirm and share
    } else if (currentCase?.shareCode && !doctorHandoffRef.current && !findDoctorRef.current) {
      setShareCode(currentCase.shareCode)
      setExpirationDate(currentCase.shareExpiration || getExpirationDate())
      if (currentCase.sharePin) setPin(currentCase.sharePin)
      setStep('generated')
    }
  }, [currentCase])

  // MD Onboard Mode: Set up for MD-initiated case that needs patient onboarding
  useEffect(() => {
    if (!isMDOnboard || !currentCase || !isInitialized) return
    
    console.log('[ShareScreen] MD Onboard mode detected, setting up...')
    setMDOnboardMode(true)
    
    // Get doctor info from case
    const doctor = currentCase.doctor
    if (doctor) {
      const doctorObj = {
        id: doctor.id,
        first_name: doctor.first_name || doctor.firstName,
        last_name: doctor.last_name || doctor.lastName,
        specialty: ((doctor.specialties && doctor.specialties[0]) || ''),
        practice_name: doctor.practice_name || doctor.practiceName,
        practice_address: doctor.practice_address,
        practice_city: doctor.practice_city,
        practice_state: doctor.practice_state,
        practice_zip: doctor.practice_zip,
        phone: doctor.phone || doctor.practice_phone,
        practice_phone: doctor.practice_phone,
        board_certified: doctor.board_certified,
        profile_photo: doctor.profile_photo,
        bio: doctor.bio,
        education: doctor.education,
        languages: doctor.languages,
        npi: doctor.npi
      }
      console.log('[ShareScreen] MD Onboard: Setting assigned doctor')
      setSelectedDoctor(doctorObj)
    }
    
    // Check for existing appointment — check multiple sources
    const appointments = currentCase.appointments || []
    let initialAppointment = appointments.find(a => {
      if (isOutsideAppointment(a)) return false
      const cat = (a.appointment_category || a.appointmentCategory || '').toLowerCase()
      const type = (a.type || '').toLowerCase()
      // Match by category first, then by type not being follow-up
      return cat === 'initial' || (!cat && !type.includes('follow'))
    })
    
    // Fallback: check followUpAppointment (API returns initial appointment here too when cat=follow_up due to bug)
    if (!initialAppointment && currentCase.followUpAppointment) {
      initialAppointment = currentCase.followUpAppointment
    }
    
    // Fallback: check scheduledAppointment
    if (!initialAppointment && currentCase.scheduledAppointment) {
      initialAppointment = currentCase.scheduledAppointment
    }
    
    // Fallback: check appointment (singular)
    if (!initialAppointment && currentCase.appointment) {
      initialAppointment = currentCase.appointment
    }
    
    // Fallback: check appointmentRequest for confirmed date
    if (!initialAppointment && currentCase.appointmentRequest?.confirmedDate) {
      initialAppointment = {
        date: currentCase.appointmentRequest.confirmedDate,
        time: currentCase.appointmentRequest.confirmedTime,
        type: currentCase.appointmentRequest.confirmedType || currentCase.appointmentRequest.appointmentType || 'in_person',
        status: 'confirmed'
      }
    }
    
    console.log('[ShareScreen] MD Onboard appointment search:', {
      appointmentsCount: appointments.length,
      followUpAppointment: !!currentCase.followUpAppointment,
      scheduledAppointment: !!currentCase.scheduledAppointment,
      appointment: !!currentCase.appointment,
      appointmentRequest: !!currentCase.appointmentRequest?.confirmedDate,
      found: !!initialAppointment
    })
    
    if (initialAppointment) {
      console.log('[ShareScreen] MD Onboard: Found existing appointment')
      const apptData = {
        date: initialAppointment.date,
        time: initialAppointment.time,
        type: initialAppointment.appointment_type || initialAppointment.type || 'in_person'
      }
      setSelectedDate(apptData.date)
      setSelectedTime(apptData.time)
      setAppointmentType(apptData.type)
      setRequestAppointment(true)
      setMDOnboardAppointment(apptData) // v191.8: Store for direct prop pass
    }
    
    // Check for existing pharmacy — case first, then profile default
    if (currentCase.preferredPharmacy) {
      console.log('[ShareScreen] MD Onboard: Found existing pharmacy')
      setSelectedPharmacy(currentCase.preferredPharmacy)
    } else if (profile?.preferredPharmacy) {
      console.log('[ShareScreen] MD Onboard: Using profile pharmacy')
      setSelectedPharmacy(profile.preferredPharmacy)
    }
    
    // Check for existing agreement
    if (currentCase.patientAgreement?.signed) {
      console.log('[ShareScreen] MD Onboard: Agreement already signed')
      setAgreementSigned(true)
      setSignature(currentCase.patientAgreement.signature)
    }
    
    // Go directly to confirm-doctor step (which has pharmacy + agreement UI)
    setStep('confirm-doctor')
    
  }, [isMDOnboard, currentCase, isInitialized])

  // Scroll to top on step change
  useEffect(() => {
    // v195.1: Use rAF to ensure DOM has rendered new step content before scrolling
    const scrollToTop = () => {
      window.scrollTo(0, 0)
      const scrollWrapper = document.querySelector('.scroll-wrapper')
      if (scrollWrapper) scrollWrapper.scrollTop = 0
    }
    scrollToTop()
    requestAnimationFrame(scrollToTop)
    setShowContent(false) // Reset animation on step change

    // v201.5: doctor-sent confirmation screen renders content after scroll fires —
    // add a delayed scroll to guarantee top position after full render
    if (step === 'doctor-sent') {
      const t = setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'instant' })
        const sw = document.querySelector('.scroll-wrapper')
        if (sw) sw.scrollTop = 0
      }, 120)
      return () => clearTimeout(t)
    }
  }, [step])

  // v225.25.0 (Item 3): the doctor-sent confirmation reveals its content only
  // after the chat-bubble animation finishes (setShowContent), which is well
  // after the step-change scroll above fired — and the actual scroller is the
  // inner overflow-auto column, not window/.scroll-wrapper. Re-pin every scroll
  // surface (window, wrapper, and the column itself) to the top the moment
  // content reveals so the screen always opens at the doctor card.
  useEffect(() => {
    if (step !== 'doctor-sent') return
    const pin = () => {
      window.scrollTo({ top: 0, behavior: 'instant' })
      const sw = document.querySelector('.scroll-wrapper')
      if (sw) sw.scrollTop = 0
      if (doctorSentScrollRef.current) doctorSentScrollRef.current.scrollTop = 0
    }
    pin()
    const r = requestAnimationFrame(pin)
    return () => cancelAnimationFrame(r)
  }, [step, showContent])

  // Profile completion calculations
  const profileStatus = calculateProfileStatus(profile)
  const { completionPercent, allergiesComplete, medicationsComplete, identificationComplete, insuranceComplete } = profileStatus
  const requiredComplete = allergiesComplete && medicationsComplete && identificationComplete && insuranceComplete

  // Load doctors when entering find-doctor step
  useEffect(() => {
    if (step === 'find-doctor') {
      loadDoctors()
    }
  }, [step])

  // v194.5: Filter doctors by search query + active filter chips (multi-select Set)
  // v213.4 (C-A-181): Distance/nearby logic now uses `activeZip` (location source)
  //                    instead of always reading profile.identification.zipCode.
  // v213.4 (C-A-153): Default-sort by next_available_date (earliest first, no-date last).
  useEffect(() => {
    let result = [...doctors]
    
    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(d => 
        (d.first_name + ' ' + d.last_name).toLowerCase().includes(query) ||
        ((d.specialties && d.specialties[0]) || '')?.toLowerCase().includes(query) ||
        d.practice_name?.toLowerCase().includes(query) ||
        d.practice_city?.toLowerCase().includes(query)
      )
    }
    
    // v194.5: Apply ALL active filters (intersection — doctor must pass every active filter)
    const ins = profile?.insurance || {}
    const patientInsurance = ins.provider || ins.planName || ins.insurerName || ins.carrierName || ins.company || ''
    
    // s101 Build 67: the chosen specialty (case's own, another the admin assigned, or Primary care).
    result = result.filter(d => doctorFitsChoice(d, activeChoice, specialtyMatchesTarget))

    // Nearby filter: within ~25 miles of the active location source.
    if (distanceFilter && !area) {
      result = result.filter(d => {
        const dist = distanceOf(d) // PROD-655
        return dist != null && dist <= distanceFilter
      })
    }
    
    // s101 Phase 4: "Search this area" — the Orella doctors inside the searched circle.
    if (area) {
      result = result.filter(d => {
        const dist = doctorDistanceMiles(d, { coords: { lat: area.lat, lng: area.lng }, zip: area.zip })
        return dist != null && dist <= (distanceFilter || 25)
      })
    }

    // Insurance filter: tag + sort (never excludes doctors with empty insurance data)
    if (insuranceActive && patientInsurance) {
      result = result.map(d => ({ ...d, _insuranceMatch: getInsuranceMatchType(d, ins) }))
      const matchOrder = { confirmed: 0, unknown: 1, no_match: 2 }
      result.sort((a, b) => (matchOrder[a._insuranceMatch] || 1) - (matchOrder[b._insuranceMatch] || 1))
    }
    
    // v194.5 Item 2: Virtual filter — use API has_video_slots (checks schedule modalities), 
    // fallback to appointment_types for backward compatibility
    if (virtualActive) {
      result = result.filter(d => {
        if (d.has_video_slots) return true
        const types = d.appointment_types || []
        return types.some(t => t === 'video' || t === 'virtual' || t === 'telehealth')
      })
    }
    
    // v213.4 (C-A-153 / C-A-180): Default-sort by next_available_date (earliest first,
    // no-date doctors last). Replaces the old soonest-available filter chip — that
    // chip is removed because this is now the default ordering.
    // If the insurance filter is active, that ordering wins (insurance match groups);
    // otherwise next_available_date is the primary sort key.
    if (!insuranceActive) {
      result.sort((a, b) => {
        const dateA = a.next_available_date || 'z'
        const dateB = b.next_available_date || 'z'
        return dateA.localeCompare(dateB)
      })
    }
    
    // Secondary sort: by distance within insurance groups (when insurance active) or as default
    if (insuranceActive && (activeZip || currentCoords)) {
      const matchOrder = { confirmed: 0, unknown: 1, no_match: 2 }
      result.sort((a, b) => {
        const matchA = a._insuranceMatch || 'unknown'
        const matchB = b._insuranceMatch || 'unknown'
        if (matchA !== matchB) return (matchOrder[matchA] || 1) - (matchOrder[matchB] || 1)
        // Within same insurance match, sort by next_available_date first, distance as tiebreaker
        const dateA = a.next_available_date || 'z'
        const dateB = b.next_available_date || 'z'
        if (dateA !== dateB) return dateA.localeCompare(dateB)
        const distA = distanceOf(a) ?? 9999 // PROD-655
        const distB = distanceOf(b) ?? 9999
        return distA - distB
      })
    }
    
    // v242.103.0 — the directory's DoctorCard reads `_distance` and `_nextSlot`.
    // ★ `_nextSlot` carries only a DATE: the picker knows next_available_date and not
    //   a time, and formatNextSlot renders "Tomorrow" / "Wed, Aug 27" for a timeless
    //   slot. Inventing a time to match the directory's "Tomorrow 10:00 AM" would show
    //   the patient an appointment nobody has offered.
    result = result.map(d => ({
      ...d,
      _distance: distanceOf(d), // PROD-655
      _nextSlot: d.next_available_date ? { date: d.next_available_date } : null,
    }))
    if (sortBy === 'distance') result.sort((a, b) => (a._distance ?? 9999) - (b._distance ?? 9999))
    else if (sortBy === 'rating') result.sort((a, b) => (Number(b.google_rating) || 0) - (Number(a.google_rating) || 0))

    setFilteredDoctors(result)
  }, [searchQuery, doctors, distanceFilter, insuranceActive, virtualActive, sortBy, activeZip, currentCoords, area, activeChoice?.id])

  // Fetch doctor availability when doctor is selected.
  // ★★★ D-g / D-b (Alex; round 2 of P1, A2). Only real slots, read through the one rule
  //   (utils/bookableSlots.js). This used to leave availableSlots null whenever the
  //   calendar was empty or the read failed ("use fallback static slots"), and
  //   ShareConversation then offered today + 14 days and a static list of office hours.
  //   Scheduling is mandatory for a send, so no slots now means NoSlotsCard, by reason.
  useEffect(() => {
    let cancelled = false
    const loadAvailability = async () => {
      if (!selectedDoctor) {
        setAvailableSlots(null)
        setSlotPlaces(null)
        setSlotsReason(null)
        setLoadingSlots(false)
        return
      }
      setLoadingSlots(true)
      const { slots, reason, places } = await bookableSlots(doctorsAPI, selectedDoctor.id, 14)
      if (cancelled) return
      if (reason) console.log('[ShareScreen] No slots to offer —', reason)
      setAvailableSlots(slots)
      setSlotPlaces(places || null)
      setSlotsReason(reason)
      setLoadingSlots(false)
    }
    
    loadAvailability()
    return () => { cancelled = true }
  }, [selectedDoctor, slotsAttempt])

  // Reset selections when the doctor changes. P1 round 6 (A1): its own effect, because a
  // re-read of the open times (slotsAttempt: "Try again", or a slot conflict on send) must
  // keep the date the patient picked; only another doctor clears it.
  useEffect(() => {
    setSelectedDate(null)
    setSelectedTime(null)
  }, [selectedDoctor])

  const loadDoctors = async () => {
    setLoadingDoctors(true)
    setError('')
    
    try {
      // Get matching specialties for this case's category
      const specialtyId = currentCase?.specialtyId
      const focusAreaId = currentCase?.focusAreaId
      const matchingSpecialties = getMatchingSpecialties(specialtyId, focusAreaId)
      
      console.log('[ShareScreen] Loading doctors for:', { specialtyId, focusAreaId, matchingSpecialties })
      
      // Fetch doctors from API
      const allDoctors = await doctorsAPI.list()
      
      // v242.75.0 (DOCTOR-MATCH-1) — the matcher moved to utils/doctorMatch.js
      // so the share-entry screen can show the same list without a second copy.
      // TF-21 came from displaying a different field than the one matched on;
      // two implementations is how that returns.
      // ★★ D-g (Alex, 2026-09-25): a send requires a time, so a physician without real
      //   hours is not selectable here at all. hasRealHours reads the API's `bookable`
      //   and falls back to next_available_date when an older API does not send it.
      // s101 Build 67: load every doctor any CHOICE can show (Primary care included); the
      //   filter below narrows to the chosen one.
      // ★★ D527 (Dr. Rami 2026-10-23, time request; handoff s116): REVERSED. Hours are no longer required; a physician
      //   who does not accept new patients is still left out.
      const availableDoctors = matchDoctorsToCase(allDoctors, [...matchingSpecialties, 'General Medicine']).filter((d) => !!d && d.accepting_patients !== false)

      // v193.9: Sort by distance from patient (closest first)
      // v213.4 (C-A-181): Uses activeZip (location source) instead of profile zip.
      // The filter useEffect will re-sort by next_available_date once filters apply,
      // so this is just an initial visible ordering.
      if (activeZip || currentCoords) {
        availableDoctors.sort((a, b) => (distanceOf(a) ?? 9999) - (distanceOf(b) ?? 9999)) // PROD-655
      }
      
      setDoctors(availableDoctors)
      setFilteredDoctors(availableDoctors)
    } catch (err) {
      console.error('Failed to load doctors:', err)
      setError(t('errors2.unableToLoadDoctors'))
    }
    
    setLoadingDoctors(false)
  }
  
  // Pharmacy search with mock data (would connect to real pharmacy API)
  const searchPharmacies = async (query) => {
    if (!query || query.length < 2) {
      setPharmacyResults([])
      return
    }
    
    setSearchingPharmacy(true)
    
    // Comprehensive pharmacy database - in production, would use a real pharmacy API (e.g., Surescripts, NCPDP)
    const pharmacyDatabase = [
      // CVS Pharmacy locations
      { id: 'cvs-90001', name: t('share.cvsPharmacy'), address: '1001 S Broadway', city: 'Los Angeles', state: 'CA', zip: '90015', phone: '(213) 747-5150', hours: 'Open 24 hours' },
      { id: 'cvs-90012', name: t('share.cvsPharmacy'), address: '628 S Spring St', city: 'Los Angeles', state: 'CA', zip: '90014', phone: '(213) 622-1175', hours: '8am-10pm' },
      { id: 'cvs-90024', name: t('share.cvsPharmacy'), address: '10861 Weyburn Ave', city: 'Los Angeles', state: 'CA', zip: '90024', phone: '(310) 208-5167', hours: 'Open 24 hours' },
      { id: 'cvs-90034', name: t('share.cvsPharmacy'), address: '10900 W Pico Blvd', city: 'Los Angeles', state: 'CA', zip: '90064', phone: '(310) 441-3630', hours: '8am-10pm' },
      { id: 'cvs-90036', name: t('share.cvsPharmacy'), address: '6150 Wilshire Blvd', city: 'Los Angeles', state: 'CA', zip: '90048', phone: '(323) 939-4121', hours: 'Open 24 hours' },
      { id: 'cvs-90046', name: t('share.cvsPharmacy'), address: '8490 W Sunset Blvd', city: 'West Hollywood', state: 'CA', zip: '90069', phone: '(323) 650-0093', hours: 'Open 24 hours' },
      { id: 'cvs-90210', name: t('share.cvsPharmacy'), address: '252 N Canon Dr', city: 'Beverly Hills', state: 'CA', zip: '90210', phone: '(310) 275-3381', hours: '8am-10pm' },
      { id: 'cvs-90401', name: t('share.cvsPharmacy'), address: '1401 2nd St', city: 'Santa Monica', state: 'CA', zip: '90401', phone: '(310) 393-1234', hours: 'Open 24 hours' },
      
      // Walgreens locations
      { id: 'wag-90001', name: t('share.walgreens'), address: '3201 Wilshire Blvd', city: 'Los Angeles', state: 'CA', zip: '90010', phone: '(213) 386-0871', hours: 'Open 24 hours' },
      { id: 'wag-90019', name: t('share.walgreens'), address: '4645 Venice Blvd', city: 'Los Angeles', state: 'CA', zip: '90019', phone: '(323) 936-4118', hours: '8am-10pm' },
      { id: 'wag-90025', name: t('share.walgreens'), address: '11925 Santa Monica Blvd', city: 'Los Angeles', state: 'CA', zip: '90025', phone: '(310) 826-8235', hours: 'Open 24 hours' },
      { id: 'wag-90028', name: t('share.walgreens'), address: '6201 Hollywood Blvd', city: 'Los Angeles', state: 'CA', zip: '90028', phone: '(323) 464-4128', hours: 'Open 24 hours' },
      { id: 'wag-90035', name: t('share.walgreens'), address: '1501 S La Cienega Blvd', city: 'Los Angeles', state: 'CA', zip: '90035', phone: '(310) 652-7621', hours: '8am-10pm' },
      { id: 'wag-90066', name: t('share.walgreens'), address: '4012 Lincoln Blvd', city: 'Marina Del Rey', state: 'CA', zip: '90292', phone: '(310) 822-7177', hours: '8am-10pm' },
      { id: 'wag-90402', name: t('share.walgreens'), address: '1501 Montana Ave', city: 'Santa Monica', state: 'CA', zip: '90403', phone: '(310) 458-5437', hours: '8am-10pm' },
      
      // Rite Aid locations
      { id: 'rite-90004', name: t('share.riteAid'), address: '5851 W Sunset Blvd', city: 'Los Angeles', state: 'CA', zip: '90028', phone: '(323) 461-0115', hours: '8am-9pm' },
      { id: 'rite-90016', name: t('share.riteAid'), address: '2600 Crenshaw Blvd', city: 'Los Angeles', state: 'CA', zip: '90016', phone: '(323) 732-4123', hours: '8am-9pm' },
      { id: 'rite-90039', name: t('share.riteAid'), address: '3021 Los Feliz Blvd', city: 'Los Angeles', state: 'CA', zip: '90039', phone: '(323) 661-1111', hours: '8am-10pm' },
      { id: 'rite-90048', name: t('share.riteAid'), address: '7900 Beverly Blvd', city: 'Los Angeles', state: 'CA', zip: '90048', phone: '(323) 653-5146', hours: '8am-9pm' },
      
      // Costco Pharmacy
      { id: 'cost-90232', name: t('share.costcoPharmacy'), address: '13463 Washington Blvd', city: 'Marina Del Rey', state: 'CA', zip: '90292', phone: '(310) 754-3016', hours: '10am-7pm' },
      { id: 'cost-90230', name: t('share.costcoPharmacy'), address: '2901 Los Feliz Blvd', city: 'Los Angeles', state: 'CA', zip: '90039', phone: '(323) 644-5217', hours: '10am-7pm' },
      { id: 'cost-91367', name: t('share.costcoPharmacy'), address: '21300 Victory Blvd', city: 'Woodland Hills', state: 'CA', zip: '91367', phone: '(818) 936-3310', hours: '10am-7pm' },
      
      // Target Pharmacy
      { id: 'tgt-90016', name: t('share.targetPharmacy'), address: '3535 S La Cienega Blvd', city: 'Los Angeles', state: 'CA', zip: '90016', phone: '(310) 558-6051', hours: '9am-9pm' },
      { id: 'tgt-90028', name: t('share.targetPharmacy'), address: '5520 W Sunset Blvd', city: 'Los Angeles', state: 'CA', zip: '90028', phone: '(323) 461-6380', hours: '9am-9pm' },
      { id: 'tgt-90401', name: t('share.targetPharmacy'), address: '302 Colorado Ave', city: 'Santa Monica', state: 'CA', zip: '90401', phone: '(310) 460-7590', hours: '9am-9pm' },
      { id: 'tgt-90025', name: t('share.targetPharmacy'), address: '11840 W Olympic Blvd', city: 'Los Angeles', state: 'CA', zip: '90064', phone: '(310) 481-0331', hours: '9am-9pm' },
      
      // Vons Pharmacy
      { id: 'vons-90024', name: t('share.vonsPharmacy'), address: '10900 Wilshire Blvd', city: 'Los Angeles', state: 'CA', zip: '90024', phone: '(310) 575-0210', hours: '9am-9pm' },
      { id: 'vons-90049', name: t('share.vonsPharmacy'), address: '17380 W Sunset Blvd', city: 'Pacific Palisades', state: 'CA', zip: '90272', phone: '(310) 454-8180', hours: '9am-9pm' },
      { id: 'vons-90027', name: t('share.vonsPharmacy'), address: '1730 N Western Ave', city: 'Los Angeles', state: 'CA', zip: '90027', phone: '(323) 462-0451', hours: '9am-9pm' },
      
      // Ralphs Pharmacy
      { id: 'ral-90048', name: t('share.ralphsPharmacy'), address: '9040 Beverly Blvd', city: 'West Hollywood', state: 'CA', zip: '90048', phone: '(310) 278-3251', hours: '9am-9pm' },
      { id: 'ral-90024', name: t('share.ralphsPharmacy'), address: '10861 Weyburn Ave', city: 'Los Angeles', state: 'CA', zip: '90024', phone: '(310) 209-0632', hours: '9am-9pm' },
      { id: 'ral-90066', name: t('share.ralphsPharmacy'), address: '12101 W Washington Blvd', city: 'Los Angeles', state: 'CA', zip: '90066', phone: '(310) 313-7500', hours: '9am-9pm' },
      
      // Walmart Pharmacy
      { id: 'wmt-90058', name: t('share.walmartPharmacy'), address: '2770 E Olympic Blvd', city: 'Los Angeles', state: 'CA', zip: '90023', phone: '(323) 859-0670', hours: '9am-9pm' },
      { id: 'wmt-90501', name: t('share.walmartPharmacy'), address: '19340 Hawthorne Blvd', city: 'Torrance', state: 'CA', zip: '90503', phone: '(310) 371-0260', hours: '9am-9pm' },
      
      // Amazon Pharmacy (Mail order)
      { id: 'amz-mail', name: t('share.amazonPharmacy'), address: 'Mail Order Delivery', city: 'Seattle', state: 'WA', zip: '98109', phone: '1-855-745-5725', hours: 'Online 24/7' },
      
      // Capsule Pharmacy (Delivery)
      { id: 'cap-la', name: t('share.capsulePharmacy'), address: 'Same-Day Delivery', city: 'Los Angeles', state: 'CA', zip: '90001', phone: '1-844-224-8826', hours: 'Online 24/7' },
      
      // Alto Pharmacy (Delivery)
      { id: 'alto-la', name: t('share.altoPharmacy'), address: 'Same-Day Delivery', city: 'Los Angeles', state: 'CA', zip: '90001', phone: '1-800-874-5881', hours: 'Online 24/7' },
    ]
    
    // Filter by query
    const lowerQuery = query.toLowerCase()
    const results = pharmacyDatabase.filter(p => 
      p.name.toLowerCase().includes(lowerQuery) ||
      p.address.toLowerCase().includes(lowerQuery) ||
      p.city.toLowerCase().includes(lowerQuery) ||
      p.zip.includes(query)
    ).slice(0, 10)  // Limit to 10 results
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300))
    
    setPharmacyResults(results)
    setSearchingPharmacy(false)
  }
  
  // Signature canvas functions
  const getCanvasCoords = (e, canvas) => {
    const rect = canvas.getBoundingClientRect()
    const clientX = e.touches ? e.touches[0].clientX : e.clientX
    const clientY = e.touches ? e.touches[0].clientY : e.clientY
    // Scale from CSS display size to canvas internal resolution
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    }
  }

  const startDrawing = (e) => {
    e.preventDefault()
    setIsDrawing(true)
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const { x, y } = getCanvasCoords(e, canvas)
    ctx.beginPath()
    ctx.moveTo(x, y)
  }
  
  const draw = (e) => {
    if (!isDrawing) return
    e.preventDefault()
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    const { x, y } = getCanvasCoords(e, canvas)
    ctx.lineTo(x, y)
    ctx.strokeStyle = '#000'
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.stroke()
    // v242.64.0 (BUG-5): one definition of "there is a signature here", shared
    // with SignAgreementScreen. Only consulted while false,
    // so the pixel scan runs a handful of times, not on every pointer move.
    if (!hasInk && canvasHasInk(canvas)) setHasInk(true)
  }
  
  const stopDrawing = () => {
    setIsDrawing(false)
  }
  
  const clearSignature = () => {
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    setSignature(null)
    setHasInk(false)
  }
  
  // v242.64.0 (BUG-5). WAS: toDataURL() unconditionally, then agreementSigned = true.
  // A blank canvas is a perfectly valid PNG, so tapping "Sign & Accept" without
  // drawing anything recorded a SIGNED Patient Care Agreement — a consent record
  // that cannot be relied on. The button is now disabled until there is ink, and
  // this is the second gate behind it: signatureDataUrl() returns null on a blank
  // canvas rather than a picture of nothing.
  const saveSignature = () => {
    const canvas = signatureCanvasRef.current
    if (!canvas) return
    const dataUrl = signatureDataUrl(canvas)
    if (!dataUrl) { setHasInk(false); return }
    if (!canAcceptSignature({ hasInk: true, practice: practiceTemplate, agreeOrella, agreePractice })) return
    setSignature(dataUrl)
    setSignedPracticeKey(practiceSignKey(practiceTemplate))
    setAgreementSigned(true)
    setShowAgreementModal(false)
  }

  const handleSendToDoctor = async () => {
    if (!selectedDoctor) return
    
    // Check profile completion first
    if (!requiredComplete) {
      setError(t('errors2.pleaseCompleteProfile'))
      return
    }
    
    // Check agreement is signed
    if (!agreementSigned) {
      setError(t('errors2.pleaseSignAgreement'))
      return
    }
    
    // Check pharmacy is selected (required)
    if (!selectedPharmacy) {
      setError(t('errors2.pleaseSelectPharmacy'))
      return
    }

    // MEET PA0: a video visit's recording question comes BEFORE anything is sent; closing the
    //   sheet sends nothing. In person, or no slot picked: no question (scripts/check-join-consent.mjs).
    const consent = requestAppointment && selectedDate && selectedTime && !referralHold
      ? await askRecordingConsent(appointmentType)
      : { proceed: true, fields: {} }
    if (!consent.proceed) return

    setSubmitPhase('sending')
    setSendingToDoctor(true)
    setError('')
    
    try {
      let caseId = currentCase.id
      const isLocalId = caseId && (caseId.startsWith('case') || caseId.startsWith('local-'))
      
      console.log('[SendToDoctor] Case ID:', caseId, 'isLocalId:', isLocalId)
      
      // If local ID, sync to server first
      if (isLocalId) {
        console.log('[SendToDoctor] Syncing local case to server...')
        try {
          const syncedCase = await casesAPI.syncCase(currentCase)
          if (syncedCase?.id) {
            caseId = syncedCase.id
            updateCase({ id: caseId })
            console.log('[SendToDoctor] Case synced, new ID:', caseId)
          }
        } catch (syncErr) {
          console.error('[SendToDoctor] Sync failed:', syncErr)
          setError(t('errors2.failedToSaveCase'))
          setSendingToDoctor(false)
          setSubmitPhase(null)
          return
        }
      }
      
      // Build appointment request if enabled
      // The contract: the patient app sends 'HH:MM' (24h). A legacy 'h:mm AM/PM' or an
      // unpadded '9:00' is normalised here, at the one place it leaves the app.
      // s106: no booking while a referral is pending (A2 'ask' / 'upload'); the server locks it too.
      const appointmentRequest = requestAppointment && selectedDate && selectedTime && !referralHold ? {
        requestedDate: selectedDate,
        requestedTime: toHHMM(selectedTime),
        appointmentType: appointmentType,
        status: 'confirmed' // v140: auto-confirmed on share
      } : null
      
      // Build pharmacy data
      const pharmacyData = selectedPharmacy ? {
        id: selectedPharmacy.id,
        name: selectedPharmacy.name,
        address: selectedPharmacy.address,
        city: selectedPharmacy.city,
        state: selectedPharmacy.state,
        zip: selectedPharmacy.zip,
        phone: selectedPharmacy.phone
      } : null
      
      // Build agreement data WITH signature for API
      const agreementData = agreementSigned ? buildPatientAgreement(signature) : null
      
      console.log('[SendToDoctor] Sending case', caseId, 'to doctor', selectedDoctor.id, 'with appointment:', !!appointmentRequest) // MEET PA5: presence only
      console.log('[SendToDoctor] Selected pharmacy:', !!pharmacyData) // PROD-903: presence only
      console.log('[SendToDoctor] Agreement signed:', agreementSigned, 'has signature:', !!signature)
      
      // CRITICAL: Sync detect data (photos/videos/documents) to DB BEFORE sending to doctor
      // The real-time detect sync in updateAndSaveCase can fail silently for large files
      // This guarantees detect files are in the DB when the MD loads the case
      try {
        const detectPhotos = currentCase.detect?.photos || []
        const detectVideos = currentCase.detect?.videos || []
        const detectDocs = currentCase.detect?.documents || []
        const totalDetectFiles = detectPhotos.length + detectVideos.length + detectDocs.length
        
        if (totalDetectFiles > 0) {
          console.log('[SendToDoctor] Syncing detect data - photos:', detectPhotos.length, 'videos:', detectVideos.length, 'documents:', detectDocs.length)
          
          // Send metadata for all files (small payload, always succeeds)
          const stripMeta = (f) => ({ id: f.id, name: f.name, type: f.type, size: f.size, uploadedAt: f.uploadedAt, hasData: !!(f.data || f.storageUrl) })
          
          // PHI: never inline base64 into the shared record. Prefer the storage
          // reference; if a legacy file still holds only base64 `data` (no
          // storageUrl), upload it to storage NOW and send the reference
          // (upload-on-share). Fail-closed: if the upload fails, send metadata
          // only — base64 never enters the shared record. The MD panel resolves
          // the reference to a presigned URL at view time.
          const { uploadToStorage } = await import('../../services/storage')
          const withRef = async (f) => {
            if (f.storageUrl) {
              return { ...stripMeta(f), url: f.storageUrl, storageUrl: f.storageUrl }
            }
            if (f.data) {
              try {
                const storageUrl = await uploadToStorage(caseId, f.name || `detect-${f.id}`, f.data, f.type)
                if (storageUrl) {
                  return { ...stripMeta(f), url: storageUrl, storageUrl }
                }
              } catch (upErr) {
                console.warn('[SendToDoctor] upload-on-share failed:', upErr?.message || upErr)
              }
            }
            return stripMeta(f)
          }
          
          const detectPayload = {
            photos: await Promise.all(detectPhotos.map(withRef)),
            videos: await Promise.all(detectVideos.map(withRef)),
            documents: await Promise.all(detectDocs.map(withRef)),
          }
          
          console.log('[SendToDoctor] Detect payload sizes:', {
            photos: detectPayload.photos.map(p => ({ hasData: !!p.data })),
            videos: detectPayload.videos.length,
            documents: detectPayload.documents.map(d => ({ hasData: !!d.data })),
          })
          
          await casesAPI.update(caseId, detectPayload)
          console.log('[SendToDoctor] Detect data synced to DB successfully')
        } else {
          console.log('[SendToDoctor] No detect files to sync')
        }
      } catch (detectErr) {
        console.warn('[SendToDoctor] Detect sync failed (non-fatal):', detectErr.message || detectErr)
        // Non-fatal: continue with send-to-doctor even if detect sync fails
      }
      
      // Send case to selected doctor via API (with optional appointment request, pharmacy, agreement)
      const response = await casesAPI.sendToDoctor(caseId, selectedDoctor.id, appointmentRequest, pharmacyData, agreementData, consent.fields)
      
      // MEET PA4 (R5 info 5): not the response — it can carry the appointment and its meeting link.
      console.log('[SendToDoctor] Response received')
      // ★★ D527 (time request): a physician without hours is never auto-accepted; the API says so with
      //   autoAcceptRefused 'time_needs_confirmation'. The patient sees the normal "case sent" step (doctor-sent),
      //   never an "accepted" state, and the local mirror records the time as a REQUEST awaiting confirmation.
      const timeNeedsConfirmation = response?.autoAcceptRefused === 'time_needs_confirmation'
      
      // Update local case state with pharmacy and agreement info (including signature for local storage)
      updateAndSaveCase({ 
        id: caseId, // Make sure ID is updated if it was synced
        sentToDoctor: selectedDoctor.id,
        sentToDoctorName: clinicianName(selectedDoctor, 'display', 'en'),   // stored: the API's language (English)
        sentToDoctorAt: new Date().toISOString(),
        doctorSource: 'orella',
        doctor_source: 'orella',
        sentToDoctorData: {
          id: selectedDoctor.id,
          first_name: selectedDoctor.first_name,
          last_name: selectedDoctor.last_name,
          // v225.11.0 (#9): doctor records carry `specialties` (array); the old
          // `specialty` (singular) was always undefined, which let downstream UI
          // fall back to the CASE's specialty area. Persist the real array.
          specialties: selectedDoctor.specialties || (selectedDoctor.specialty ? [selectedDoctor.specialty] : []),
          specialty: (selectedDoctor.specialties && selectedDoctor.specialties[0]) || selectedDoctor.specialty || '',
          board_certified: selectedDoctor.board_certified,
          profile_photo: selectedDoctor.profile_photo,
          practice_name: selectedDoctor.practice_name,
          practice_address: selectedDoctor.practice_address,
          practice_city: selectedDoctor.practice_city,
          practice_state: selectedDoctor.practice_state,
          practice_zip: selectedDoctor.practice_zip,
          phone: selectedDoctor.phone || selectedDoctor.practice_phone,
          bio: selectedDoctor.bio,
          education: selectedDoctor.education,
          languages: selectedDoctor.languages,
          npi: selectedDoctor.npi,
        },
        status: 'shared',
        // ★ v242.207.0 — send_case_to_doctor now clears the case's share code
        //   server-side (PROD-179); mirror it so no screen shows a dead code as live.
        shareCode: null,
        share_code: null,
        sharePin: null,
        // ★★★ PROD-186 — NO appointmentRequest HERE. updateAndSaveCase PUTs every key
        //   in DURABLE_CASE_FIELDS, and appointmentRequest is one of them. Measured on
        //   production case logs: send_case_to_doctor wrote appointment_request with
        //   status 'pending' + pendingDoctorAcceptance, and 1.1 s later THIS call PUT
        //   the client's 4-key object (status 'confirmed', no pendingDoctorAcceptance)
        //   over it — so the doctor's accept never confirmed the appointment and the
        //   patient could never check in. After a send, appointment_request is
        //   SERVER-OWNED; the client mirrors it locally only (below).
        //   Gate: scripts/check-appointment-request-owner.mjs.
        // Add pharmacy info
        preferredPharmacy: pharmacyData,
        // Add agreement info (with signature for local)
        patientAgreement: buildPatientAgreement(signature)
      })

      // PROD-186 — LOCAL mirror only (updateCase never calls the API). Take the
      // server's own appointment_request; if that read fails, keep the request
      // locally as before — the next loadCases replaces it from the API.
      if (appointmentRequest) {
        let serverRequest = null
        try {
          const fresh = await casesAPI.get(caseId)
          const raw = fresh?.appointmentRequest || fresh?.appointment_request || null
          serverRequest = typeof raw === 'string' ? JSON.parse(raw) : raw
        } catch (readErr) {
          console.warn('[SendToDoctor] appointment_request read-back failed (non-fatal):', readErr?.message || readErr)
        }
        updateCase({ appointmentRequest: serverRequest || (timeNeedsConfirmation ? { ...appointmentRequest, status: 'pending', timeRequestOnly: true } : appointmentRequest) })
      }

      // Show success overlay then transition
      setSubmitPhase('success')
      await new Promise(r => setTimeout(r, 1200))
      setSubmitPhase('exit')
      await new Promise(r => setTimeout(r, 500))
      // s106 (A3 / A7): the case is with the specialist; now the referral request, or the upload.
      if (referralHold) {
        setSubmitPhase(null)
        navigate(referralChoice === 'ask' ? '/case/referral-request' : '/case/referral-upload',
          { state: { doctorName: clinicianName(selectedDoctor) } })
        return
      }
      setStep('doctor-sent')
      setSubmitPhase(null)
    } catch (err) {
      console.error('Failed to send to doctor:', err)
      // TEST 1.35 (D-c) — the API refuses a slot for a physician with no real hours
      //   with 409 {code:'doctor_has_no_hours'}. That is NOT "the slot was just taken":
      //   sending the patient back to pick another time from the same doctor is a loop.
      //   The contract codes are mapped first; the generic 409 below keeps its meaning.
      const refusalKey = appointmentErrorKey(err)
      if (refusalKey && refusalKey !== 'errors2.timeSlotBooked') {
        setError(t(refusalKey))
        setSubmitPhase(null)
        setSendingToDoctor(false)
        return
      }
      // v140.0: Handle scheduling conflicts (409) — return user to slot picker
      // P1 round 6 (A1): the taken TIME is cleared and the date kept; the conversation is
      //   sent back to its time picker (slotConflictSeq) and the open times are read again
      //   (slotsAttempt). This used to clear both behind the conversation's back (its review
      //   card still showed the taken slot) and returned with sendingToDoctor still true,
      //   so Send stayed a spinner.
      if (err.status === 409 || err.conflict) {
        setError(t('errors2.timeSlotBooked'))
        setSelectedTime(null)
        setSlotConflictSeq((n) => n + 1)
        setSlotsAttempt((n) => n + 1)
        setSubmitPhase(null)
        setSendingToDoctor(false)
        // Stay on the current step so user can pick a new time
        return
      }
      setError(err.message || t('share.failedToSend'))
      setSubmitPhase(null)
    }
    
    setSendingToDoctor(false)
  }

  // Handle MD onboard completion - saves pharmacy/agreement without sending to doctor (already assigned)
  const handleMDOnboardComplete = async () => {
    // Check profile completion first
    if (!requiredComplete) {
      setError(t('errors2.pleaseCompleteProfile2'))
      return
    }
    
    // Check agreement is signed
    if (!agreementSigned) {
      setError(t('errors2.pleaseSignAgreement'))
      return
    }
    
    // Check pharmacy is selected (required)
    if (!selectedPharmacy) {
      setError(t('errors2.pleaseSelectPharmacy'))
      return
    }

    // MEET PA0: as in handleSendToDoctor — asked before anything is sent; closing sends nothing.
    const consent = requestAppointment && selectedDate && selectedTime
      ? await askRecordingConsent(appointmentType)
      : { proceed: true, fields: {} }
    if (!consent.proceed) return

    setSendingToDoctor(true)
    setError('')
    setSubmitPhase('sending') // v192.0: Trigger submission animation
    
    try {
      const caseId = currentCase.id
      
      // Build pharmacy data (required)
      const pharmacyData = {
        id: selectedPharmacy.id,
        name: selectedPharmacy.name,
        address: selectedPharmacy.address,
        city: selectedPharmacy.city,
        state: selectedPharmacy.state,
        zip: selectedPharmacy.zip,
        phone: selectedPharmacy.phone
      }
      
      // Build agreement data (signature stored separately or truncated for API)
      const agreementData = buildPatientAgreement(signature)
      
      // Build appointment request if changed/selected
      let appointmentRequestData = null
      if (requestAppointment && selectedDate && selectedTime) {
        // Check if this is a reschedule (different from existing appointment)
        const existingAppts = currentCase.appointments || []
        const existingInitial = existingAppts.find(a => {
          if (isOutsideAppointment(a)) return false
          const type = (a.type || '').toLowerCase()
          return !type.includes('follow')
        })
        
        // Times are compared in one form: a stored '09:00' and a picked '9:00 AM' are the
        // same appointment, not a reschedule.
        const isReschedule = existingInitial && 
          (existingInitial.date !== selectedDate || toHHMM(existingInitial.time) !== toHHMM(selectedTime))
        
        appointmentRequestData = {
          requestedDate: selectedDate,
          requestedTime: toHHMM(selectedTime),
          appointmentType: appointmentType,
          status: isReschedule ? 'requested' : 'confirmed',
          isReschedule: isReschedule,
          originalDate: isReschedule ? existingInitial.date : null,
          originalTime: isReschedule ? existingInitial.time : null
        }
      }
      
      console.log('[MDOnboard] Saving onboarding data for case:', caseId)
      console.log('[MDOnboard] Pharmacy present:', !!pharmacyData?.name)
      console.log('[MDOnboard] Agreement signed:', !!agreementData.signed, 'signature length:', signature?.length || 0)
      console.log('[MDOnboard] Appointment request:', !!appointmentRequestData) // MEET PA5: presence only
      
      // v199.2: Use dedicated complete-onboarding endpoint which creates
      // appointment rows in the appointments table, checks slot conflicts,
      // creates Zoom meetings, and sends notifications.
      // The old casesAPI.update() only wrote appointment_request JSONB
      // but never created an actual appointment row.
      const onboardingPayload = {
        preferredPharmacy: pharmacyData,
        patientAgreement: agreementData,
      }
      
      // Only add appointmentRequest if we have one
      if (appointmentRequestData) {
        onboardingPayload.appointmentRequest = appointmentRequestData
        Object.assign(onboardingPayload, consent.fields) // MEET PA0: { recordingConsent, consentVersion } or {}
      }
      
      console.log('[MDOnboard] Onboarding payload keys:', Object.keys(onboardingPayload))
      
      // Call dedicated onboarding endpoint
      const onboardingResult = await casesAPI.completeOnboarding(caseId, onboardingPayload)
      // ★★ P1 round 5 (M4) — a 2xx is not a booked visit. complete_onboarding saves the
      //   pharmacy and the agreement and answers appointmentCreated:false when the
      //   appointments row was not written; the success animation then told the patient a
      //   visit was booked. Only meaningful when a slot was sent.
      const appointmentNotSaved = !!appointmentRequestData && onboardingResult?.appointmentCreated === false
      // MEET PA5: a boolean only — the result carries the booked row (a Meet row holds meeting_url).
      console.log('[MDOnboard] Onboarding result received, appointment not saved:', appointmentNotSaved)
      
      // Update local state with appointment data from API response
      const localUpdate = {
        preferredPharmacy: pharmacyData,
        patientAgreement: agreementData,
        onboardingComplete: true,
        doctorSource: 'orella',
        doctor_source: 'orella',
      }
      
      // ★★★ PROD-192 — NO appointmentRequest IN localUpdate. updateAndSaveCase PUTs
      //   every key in DURABLE_CASE_FIELDS, and this block used to put a client-built
      //   request with status 'confirmed' there — over what complete-onboarding had
      //   just written (confirmedAt, zoomLink), and turning a reschedule's 'requested'
      //   into a client-invented 'confirmed'. complete-onboarding WRITES
      //   appointment_request itself; the client only mirrors it (below).
      //   Gate: scripts/check-appointment-request-owner.mjs.
      
      // If appointment was created, update status locally
      if (onboardingResult?.appointmentCreated) {
        localUpdate.status = 'scheduled'
        localUpdate.appointment = onboardingResult.appointment
      }
      
      updateAndSaveCase(localUpdate)

      // PROD-192 — LOCAL mirror only (updateCase never calls the API): the server's
      // own appointment_request, read back. If the read fails, nothing is invented;
      // the next loadCases brings the server's value.
      if (appointmentRequestData) {
        try {
          const serverRequest = await readServerAppointmentRequest(caseId)
          if (serverRequest) updateCase({ appointmentRequest: serverRequest })
        } catch (readErr) {
          console.warn('[MDOnboard] appointment_request read-back failed (non-fatal):', readErr?.message || readErr)
        }
      }
      
      if (appointmentNotSaved) {
        console.warn('[MDOnboard] Onboarding saved, appointment NOT created (appointmentCreated:false)')
        setSubmitPhase(null)
        setOnboardAppointmentNotSaved(true)
        setSendingToDoctor(false)
        return
      }

      console.log('[MDOnboard] Onboarding complete, showing success animation')
      
      // v192.0: Show success animation then navigate to CaseHome
      // CaseHome will show Describe as active (describeComplete is false)
      setSubmitPhase('success')
      await new Promise(r => setTimeout(r, 1200))
      setSubmitPhase('exit')
      await new Promise(r => setTimeout(r, 500))
      setSubmitPhase(null)
      // v242.34.0 (FB): MD-initiated onboarding finished — take the patient straight
      // into Describe rather than case home. Landing on case home left them to start
      // Describe manually, and some stalled there without proceeding. Falls back to
      // case home if Describe is somehow already complete.
      if (!currentCase?.describeComplete) {
        navigate('/case/describe/voice', { replace: true })
      } else {
        navigate('/case/home', { replace: true })
      }
      
    } catch (err) {
      console.error('[MDOnboard] Failed to complete onboarding:', err)
      // TEST 1.35 (D-c), round 2 — complete_onboarding WITH a slot is refused with 409
      //   {code:'doctor_has_no_hours'} when the physician has no real hours. That is not
      //   "the slot was just booked": clearing the picker and asking for another time
      //   from the same physician is a loop the patient cannot leave. The contract codes
      //   are mapped first, the picker is left as it was, and the generic 409 below keeps
      //   its meaning (slot_conflict maps to the same "just booked" copy and falls through).
      const refusalKey = appointmentErrorKey(err)
      if (refusalKey && refusalKey !== 'errors2.timeSlotBooked') {
        setError(t(refusalKey))
        setSubmitPhase(null)
        setSendingToDoctor(false)
        return
      }
      // v199.2: Handle scheduling conflicts (409) — return user to appointment picker
      // P1 round 6 (A1): as in handleSendToDoctor, keep the date, clear the taken time,
      //   send the conversation back to its picker and read the open times again.
      if (err.status === 409 || err.conflict) {
        setError(t('errors2.timeSlotBooked') || 'That time slot was just booked. Please pick a different time.')
        setSelectedTime(null)
        setSlotConflictSeq((n) => n + 1)
        setSlotsAttempt((n) => n + 1)
        setSubmitPhase(null)
        setSendingToDoctor(false)
        return
      }
      setError(err.message || t('errors2.failedToSaveInfo'))
      setSubmitPhase(null)
    }
    
    setSendingToDoctor(false)
  }

  const handleGenerateCode = async (withPin = false) => {
    const requirePin = typeof withPin === 'boolean' ? withPin : false
    // Pharmacy is required before a share code can be generated.
    if (!selectedPharmacy) {
      setError(t('errors2.pleaseSelectPharmacy'))
      return
    }
    setGenerating(true)
    setError('')
    
    try {
      let caseId = currentCase.id
      const isLocalId = caseId && (caseId.startsWith('case') || caseId.startsWith('local-'))
      
      // If local ID, sync to server first
      if (isLocalId) {
        try {
          const syncedCase = await casesAPI.syncCase(currentCase)
          if (syncedCase?.id) {
            caseId = syncedCase.id
            updateCase({ id: caseId })
          }
        } catch (syncErr) {
          console.error('Sync failed:', syncErr)
        }
      }
      
      // Prepare pharmacy data for API
      const pharmacyData = selectedPharmacy ? {
        id: selectedPharmacy.id,
        name: selectedPharmacy.name,
        address: selectedPharmacy.address,
        city: selectedPharmacy.city,
        state: selectedPharmacy.state,
        zip: selectedPharmacy.zip,
        phone: selectedPharmacy.phone
      } : null
      
      // Prepare agreement data for API
      const agreementData = agreementSigned ? buildPatientAgreement(signature) : null
      
      console.log('[GenerateCode] Pharmacy present:', !!pharmacyData?.name)
      console.log('[GenerateCode] Agreement signed:', agreementSigned, 'has signature:', !!signature)
      
      const response = await casesAPI.generateShareCode(caseId, requirePin, pharmacyData, agreementData)
      
      const code = response.shareCode
      const pinCode = response.pin
      const expDate = response.expiresAt ? formatDateTime(response.expiresAt) : getExpirationDate()
      
      setShareCode(code)
      setPin(pinCode || '')
      setExpirationDate(expDate)
      
      // Update local case state with share code, pharmacy, and agreement info
      // v153.0: MD-agnostic path keeps status as-is (ready_to_share). Only claimed when doctor enters code.
      updateAndSaveCase({ 
        shareCode: code, 
        sharePin: pinCode,
        shareExpiration: expDate,
        isShared: true,
        // Add pharmacy info
        preferredPharmacy: pharmacyData,
        // Add agreement info
        patientAgreement: agreementData
      })
      
      setStep('generated')
    } catch (err) {
      console.error('Share code generation failed:', err)
      if (err.status === 401) {
        setError(t('errors2.pleaseLoginToShare'))
        return
      }
      setError(err.error || err.message || t('errors2.failedToGenerate'))
    } finally {
      setGenerating(false)
    }
  }

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(shareCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.log('Copy failed')
    }
  }

  const handleShareViaEmail = () => {
    const subject = `Access my Orella Health case`
    const body = `Hi,\n\nI've shared my health case with you via Orella Health.\n\nShare Code: ${shareCode}\n${pin ? `PIN: ${pin}\n` : ''}\nExpires: ${expirationDate}\n\nYou can access my case at: ${buildClaimUrl(shareCode)}\n\nThank you!`
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  const handleShareViaText = () => {
    const message = `Hi! I've shared my Orella Health case with you.\nCode: ${shareCode}${pin ? `\nPIN: ${pin}` : ''}\nExpires: ${expirationDate}\nAccess at: ${buildClaimUrl(shareCode)}`
    window.location.href = `sms:?body=${encodeURIComponent(message)}`
  }

  // Loading state
  if (!isInitialized) {
    return (
      <div className="min-h-full bg-white flex items-center justify-center">
        <div className="text-center">
          <OrellaSpinner size={32} className="mx-auto mb-2" />
          <p className="text-gray-600">{t('share.loadingYourCase')}</p>
        </div>
      </div>
    )
  }

  if (!currentCase) {
    // Try to get the most recent case if available
    if (cases.length > 0) {
      const recentCase = cases[0] // Cases are typically sorted newest first
      setCurrentCase(recentCase)
      // Don't navigate, let the effect re-run with the new case
      return (
        <div className="min-h-full bg-white flex items-center justify-center">
          <div className="text-center">
            <OrellaSpinner size={32} className="mx-auto mb-2" />
            <p className="text-gray-600">{t('share.loadingYourCase')}</p>
          </div>
        </div>
      )
    }
    // No cases at all - navigate to cases list
    navigate('/cases', { replace: true })
    return null
  }

  // ================== SHARED: Case info for compact header ==================
  const caseInfo = `${formatSpecialtyName(currentCase?.specialtyName, t)} › ${currentCase?.focusAreaName || ''}`

  // ★★ v242.105.0 — ONE derivation of the case's locked specialty, read by the
  //    chip row AND the filter sheet. It was computed inline at the chip row and
  //    the sheet was given nothing, which is how the two came to disagree in the
  //    first place: the row said "Otolaryngology (ENT)", the sheet said "All
  //    specialties". Two copies of a derivation is two answers waiting to happen.
  // ★★ v242.206.0 (TEST 1.34) — the case's specialty is `{ name }` (the store
  //    maps it so), so it is read by NAME, as every other reader does. Passing the
  //    object through crashed the picker with React #31 on its first render, when
  //    filteredDoctors is still empty. A string specialty is still accepted.
  const lockedSpecialtyLabel = formatProviderSpecialty(
    currentCase?.matchedSpecialty || filteredDoctors[0]?.matchedSpecialty
    || currentCase?.specialty?.name
    || (typeof currentCase?.specialty === 'string' && currentCase.specialty) || '', t) || null
  // ★ Only when BOTH halves exist — `caseInfo` renders "Ears › " on a case with
  //   no focus area, and a caption ending in a separator reads as a truncation.
  const lockedSpecialtyCaption = currentCase?.focusAreaName ? caseInfo : null

  // v217.1.7: The doctor profile bottom sheet. Previously this was rendered
  // only inside the final fallback return (the "Generated / Code Ready" step),
  // which meant any "View Profile" button on the find-doctor list or the
  // confirm-doctor card fired setFullProfileSheet but the sheet had no JSX to
  // render. We now render this helper in every step that has a View Profile
  // trigger so the sheet always opens.
  // ★★ TEST 3.15 (patient push 3a): the chosen physician's profile sheet, opened from the send
  //   conversation, offers the conversation's PENDING step (utils/shareStepCta.js) and does it:
  //   the send only when sending is next, the agreement when that is next, otherwise the sheet
  //   closes onto the step. It used to say "Continue" and only close. Elsewhere the sheet keeps
  //   its own CTA. scripts/check-profile-sheet-cta.mjs holds it.
  const runPendingStep = (action) => {
    setFullProfileSheet(null)
    if (action === 'send') {
      if (sendingToDoctor) return
      return (mdOnboardMode ? handleMDOnboardComplete : handleSendToDoctor)()
    }
    if (action === 'agreement') { setShowAgreementModal(true); return }
    setTimeout(() => { document.querySelector('[data-share-active-step]')?.scrollIntoView({ behavior: 'smooth', block: 'start' }) }, 320)
  }
  const selectedSheetCta = () => {
    if (step !== 'confirm-doctor' || !selectedDoctor || selectedDoctor.id !== fullProfileSheet?.doctorId) return undefined
    const cta = pendingStepCta(conversationStep, clinicianName(selectedDoctor, 'short'))
    return { label: t(cta.key, cta.params), onPress: () => runPendingStep(cta.action) }
  }

  // s110: the one way a directory doctor is sent to: straight on for one office, through the picker for several.
  const sendToDirectory = (provider) => {
    if (provider && otherOffices(provider) > 0 && !provider.officeChosen) { setOfficePick(provider); return }
    navigate('/case/share-hub', { state: { next: { to: '/case/send-report', state: { provider } } } })
  }
  const pickerZip = area && area.zip ? area.zip : (activeZip || homeZip)
  // PROD-1187: the same origin as the Doctors tab (the patient's point when known, else the area / ZIP).
  const distanceOrigin = milesOrigin(mapCenter, area)
  const pickerOrigin = distanceOrigin
  const renderOfficePicker = () => officePick ? (
    <OfficePickerSheet provider={officePick} t={t}
      zip={pickerZip}
      origin={pickerOrigin}
      onClose={() => setOfficePick(null)}
      onChoose={(provider) => { setOfficePick(null); navigate('/case/share-hub', { state: { next: { to: '/case/send-report', state: { provider } } } }) }} />
  ) : null

  const renderProfileSheet = () => (
    <BottomSheet
      open={!!fullProfileSheet}
      onClose={() => setFullProfileSheet(null)}
      maxHeight="92dvh"
    >
      {fullProfileSheet && (
        <DoctorProfileScreen
          asSheet
          doctorIdProp={fullProfileSheet.doctorId}
          modeProp={fullProfileSheet.mode}
          isSelectedProp={selectedDoctor?.id === fullProfileSheet.doctorId}
          selectedCtaProp={selectedSheetCta()}
          onCloseProp={() => setFullProfileSheet(null)}
          onPrimaryActionProp={(doctorObj) => {
            setSelectedDoctor(doctorObj)
            setStep('confirm-doctor')
            setFullProfileSheet(null)
          }}
        />
      )}
    </BottomSheet>
  )

  // ★ TEST 3.11 (patient push 3a): the location sheet is a render helper, like
  //   renderProfileSheet above, because its bar (DoctorLocationBar) is on the FIND-DOCTOR
  //   step and the sheet was rendered only inside the later confirm-doctor return. A tap on
  //   "Showing doctors near …" set showLocationSheet and nothing was drawn. Every step
  //   that renders the bar renders this. scripts/check-location-sheet.mjs holds it.
  // v213.4 (C-A-181): Location source picker — patient chooses Home /
  // Current location (browser geolocation) / a manually typed ZIP.
  const renderLocationSheet = () => (
    showLocationSheet && createPortal(
      <div
        className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-3 bottom-actions [--ba-min:12px]"
        style={{ animation: 'shareSheetFade 0.18s ease-out' }}
        onClick={() => setShowLocationSheet(false)}
      >
        {/* v230.2.5 — Entrance animation so the sheet feels instant and
            intentional instead of popping in after the parent re-render.
            Backdrop fades, panel slides up. v233.0.0 — keyframes moved to
            global index.css; injecting them inline here meant iOS didn't
            register them before first paint, so the sheet sat at opacity:0
            (mounted but invisible) until a later repaint. */}
        <div
          className="bg-white rounded-2xl w-full max-w-md p-4 sm:p-5"
          style={{ animation: 'shareSheetRise 0.26s cubic-bezier(0.2, 0.8, 0.2, 1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-bold" style={{ color: '#15354F' }}>
              {t('share.searchLocationTitle') || 'Where should we search?'}
            </h3>
            <button onClick={() => setShowLocationSheet(false)} className="text-gray-400">
              <X size={20} />
            </button>
          </div>

          {/* Home address option */}
          <button
            onClick={() => { setLocationSource('home'); setLocationError(''); setShowLocationSheet(false) }}
            disabled={!homeZip}
            className="w-full flex items-start gap-3 p-3 rounded-xl mb-2 text-left disabled:opacity-50"
            style={{
              backgroundColor: locationSource === 'home' ? '#E9F8FF' : 'white',
              border: `1.5px solid ${locationSource === 'home' ? '#46A8F9' : '#E2E8F0'}`
            }}
          >
            <Home size={20} style={{ color: '#46A8F9', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold" style={{ color: '#15354F' }}>
                {t('share.homeAddress') || 'Home address'}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {homeZip
                  ? `${t('share.fromProfile') || 'From your profile'} · ${homeZip}`
                  : (t('share.noHomeAddress') || 'No home address in profile yet')
                }
              </div>
            </div>
            {locationSource === 'home' && <Check size={16} style={{ color: '#46A8F9', flexShrink: 0 }} />}
          </button>

          {/* Current location option */}
          <button
            onClick={requestCurrentLocation}
            disabled={locationLoading}
            className="w-full flex items-start gap-3 p-3 rounded-xl mb-2 text-left disabled:opacity-50"
            style={{
              backgroundColor: locationSource === 'current' ? '#E9F8FF' : 'white',
              border: `1.5px solid ${locationSource === 'current' ? '#46A8F9' : '#E2E8F0'}`
            }}
          >
            <Navigation size={20} style={{ color: '#46A8F9', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold" style={{ color: '#15354F' }}>
                {t('share.currentLocation') || 'Use my current location'}
              </div>
              <div className="text-[11px] text-gray-500 mt-0.5">
                {locationLoading
                  ? (t('share.gettingLocation') || 'Getting your location…')
                  : (locationSource === 'current' && currentLocationLabel
                    ? `${t('share.detected') || 'Detected'} · ${currentLocationLabel}`
                    : (t('share.useGeoHint') || "Helpful when you're away from home"))
                }
              </div>
            </div>
            {locationSource === 'current' && !locationLoading && <Check size={16} style={{ color: '#46A8F9', flexShrink: 0 }} />}
            {locationLoading && <OrellaSpinner size={16} />}
          </button>

          {/* Manual ZIP entry */}
          <div
            className="p-3 rounded-xl mb-2"
            style={{
              backgroundColor: locationSource === 'manual' ? '#E9F8FF' : 'white',
              border: `1.5px solid ${locationSource === 'manual' ? '#46A8F9' : '#E2E8F0'}`
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={18} style={{ color: '#46A8F9' }} />
              <div className="text-[13px] font-semibold" style={{ color: '#15354F' }}>
                {t('share.enterZip') || 'Enter a ZIP code'}
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode="numeric"
                maxLength={5}
                pattern="[0-9]*"
                value={manualZip}
                onChange={(e) => setManualZip((e.target.value || '').replace(/[^0-9]/g, '').slice(0, 5))}
                placeholder="00000"
                className="flex-1 px-3 py-2 rounded-lg text-[13px] focus:outline-none"
                style={{ border: '1.5px solid #E2E8F0', color: '#15354F' }}
              />
              <button
                onClick={() => {
                  if (manualZip.length === 5) {
                    setLocationSource('manual')
                    setLocationError('')
                    setShowLocationSheet(false)
                  }
                }}
                disabled={manualZip.length !== 5}
                className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: '#15354F' }}
              >
                {t('common.use') || 'Use'}
              </button>
            </div>
          </div>
        </div>
      </div>,
      document.body
    )
  )

  // ================== STEP: Find Doctor ==================
  // First check: If profile incomplete, show profile completion screen
  if (step === 'find-doctor' && !requiredComplete) {
    const sectionsRemaining = 4 - [allergiesComplete, medicationsComplete, identificationComplete, insuranceComplete].filter(Boolean).length
    return (
      <div className="tabbar-screen-min-h bg-white flex flex-col">
        <ChatKeyframes />
        <CompactHeader stepNum={3} totalSteps={3} stepLabel="Share" caseInfo={caseInfo} onBack={() => caseBack(navigate, { skip: shareBackSkip })} onNotification={() => navigate('/notifications')} />
        <OrellaChatBubble
          message={t('share.chatProfileIncomplete') || "Before we share your case, let's make sure your profile has everything your doctor will need. Just a few quick details."}
          animationKey={`share-profile-${currentCase?.id}`}
          onComplete={() => setShowContent(true)}
        />
        <div className="flex-1 px-4 pt-2 pb-4 overflow-auto" style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}>
          {/* Progress bar */}
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-[11px] font-bold text-[#15354F]">{t('share.profileCompletion')}</span>
              <span className="text-[11px] font-bold text-amber-600">{completionPercent}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600 transition-all" style={{ width: `${completionPercent}%` }} />
            </div>
          </div>

          {[
            { key: 'allergies', complete: allergiesComplete, label: t('profile.allergies') },
            { key: 'medications', complete: medicationsComplete, label: t('profile.medications') },
            { key: 'identification', complete: identificationComplete, label: t('profile.identification') },
            { key: 'insurance', complete: insuranceComplete, label: t('profile.insurance') },
          ].map(item => (
            <div 
              key={item.key} 
              className={`flex items-center gap-3 p-3 rounded-xl mb-2 ${
                item.complete ? 'bg-green-50 border-[1.5px] border-green-200' : 'bg-amber-50 border-[1.5px] border-amber-200'
              }`}
            >
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${item.complete ? 'bg-green-100' : 'bg-amber-100'}`}>
                {item.complete ? <Check className="text-green-600" size={14} strokeWidth={3} /> : <AlertTriangle className="text-amber-600" size={14} />}
              </div>
              <span className={`flex-1 text-[13px] font-semibold ${item.complete ? 'text-green-700' : 'text-amber-800'}`}>{item.label}</span>
              <span className={`text-[9px] font-bold px-2 py-0.5 rounded-md ${item.complete ? 'bg-green-100 text-green-700' : 'bg-amber-200 text-amber-700'}`}>
                {item.complete ? 'Complete' : 'Required'}
              </span>
            </div>
          ))}
          <p className="text-[11px] text-gray-400 text-center mt-2">{sectionsRemaining} section{sectionsRemaining !== 1 ? 's' : ''} remaining</p>
        </div>

        {/* PROD-1194 part C: the CTA is pinned on top of the TabBar; its secondaries ("Continue without
            insurance" when only insurance is missing, and "Back to Case Home") follow the bar and scroll
            (the part-A rule: with more than one secondary only the primary is pinned). */}
        <BottomActionBar aboveTabBar className="[--bar-px:16px]" style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}>
          <button
            onClick={() => { saveFormStateToSession(); navigate(`/profile?returnToShare=true&caseId=${currentCase?.id}${mdOnboardMode ? '&mdOnboard=true' : ''}`); }}
            className="w-full py-3 rounded-full font-bold text-[15px] flex items-center justify-center gap-2 text-white"
            style={{ backgroundColor: '#D97706' }}
          >
            {t('share.completeProfileCta')} <ChevronRight size={18} />
          </button>
        </BottomActionBar>
        <div className="px-4 pb-4 bg-white" data-footer-rest style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}>
          {/* PROD-1132 Q1 (Alex 2026-10-18): self-pay right here, offered only when insurance is the one
              missing section (the real list, review MEDIUM-1); a saved answer makes insuranceComplete true
              from the store and the doctor list opens. */}
          <ContinueWithoutInsurance missing={missingBeforeShare(profile)} className="mt-1" />
          <button onClick={() => navigate('/case/home')} className="w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] text-[#15354F] text-[14px] font-semibold mt-1 flex items-center justify-center gap-1.5">{t('share.backToCaseHome')}</button>
        </div>
      </div>
    )
  }
  
  // Second: Show doctor list when profile is complete
  if (step === 'find-doctor') {
    const specialtyId = currentCase?.specialtyId
    const focusAreaId = currentCase?.focusAreaId
    const matchingSpecialties = getMatchingSpecialties(specialtyId, focusAreaId)
    
    return (
      <>
      <div className="min-h-full bg-white flex flex-col">
        <ChatKeyframes />
        <CompactHeader stepNum={3} totalSteps={3} stepLabel="Share" caseInfo={caseInfo} onBack={() => caseBack(navigate, { skip: shareBackSkip })} onNotification={() => navigate('/notifications')} />
        <OrellaChatBubble
          message={!loadingDoctors && filteredDoctors.length === 0
            // s114 (Dr. Rami 2026-10-22, en + es approved): a case already prepped (the hub's find-a-doctor path, or a case
            //   with a code) is told to pick a doctor, never to prep the case again.
            ? ((findDoctorRef.current || currentCase?.shareCode || currentCase?.share_code)
              ? t('share.chatNoDoctorsPrepped')
              : (t('share.chatNoDoctors') || `There's no matching Orella specialist in your area yet — no problem. Let's prep your case first so it's ready to bring to any doctor you choose. You'll add the doctor you're seeing at the end.`))
            : t('share.chatFindDoctor')
          }
          animationKey={`share-doctors-${currentCase?.id}-${filteredDoctors.length === 0 ? 'empty' : 'list'}`}
          onComplete={() => setShowContent(true)}
        />

        {/* Search Bar */}
        <div className="px-4 mt-3 mb-2">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" size={18} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('share.searchDoctors')}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-[13px] focus:outline-none focus:border-orella-500"
            />
          </div>
        </div>

        {/* v242.103.0 — the directory's location bar. The picker keeps its OWN
            location sheet (home / current / manual); only the presentation is shared,
            because the two screens choose a location differently. */}
        <div className="px-4 mb-2">
          <DoctorLocationBar
            userLocation={{ label: locationSource === 'current'
              ? (currentLocationLabel || t('share.searchingNearCurrent', 'My current location'))
              : (locationSource === 'manual'
                  ? (manualZip || '—')
                  : (homeZip ? `${homeZip} · ${t('doctors.home', { defaultValue: 'Home' })}` : t('share.searchingNoLocation', 'No location set'))) }}
            setShowLoc={setShowLocationSheet}
          />
          {locationError && (
            <div className="mt-1.5 text-[11px] text-amber-700 flex items-start gap-1.5 px-1">
              <AlertTriangle size={12} className="flex-shrink-0 mt-px" />
              <span>{locationError}</span>
            </div>
          )}
        </div>

        {/* v242.103.0 — the directory's chip row, its filter sheet and its List/Map
            toggle. Specialty is locked: the case already chose it. */}
        <div className="px-4 mb-3">
          <DoctorFilterChips
            setShowFilters={setShowFilters}
            distanceFilter={distanceFilter}
            specialtyFilter={null}
            specialtyLocked
            lockedSpecialtyLabel={lockedSpecialtyLabel}
            specialtyChoices={specialtyChoices} specialtyChoiceId={activeChoice.id}
            patientHasInsurance={patientHasInsurance}
            insuranceActive={insuranceActive} setInsuranceActive={setInsuranceActive}
            virtualActive={virtualActive} setVirtualActive={setVirtualActive}
            activeFilterCount={activeFilterCount}
          />
          {MAPS_KEY && <DoctorViewToggle viewMode={viewMode} setViewMode={setViewMode} />}
          {viewMode === 'map' && MAPS_KEY && (
            <div className="rounded-2xl overflow-hidden mb-3 relative" style={{ border: '1px solid #E7EEF4', height: 300 }}>
              {/* ★ s101 Phase 4: ONE map — the Orella pins and the directory doctors of the section
                  below (small pins; one ZIP's offices spread on a ring so each can be tapped),
                  fitted to the search distance (x_23), not to every pin in the country. */}
              <DoctorsMap
                doctors={[...(showFilter === 'other' ? [] : filteredDoctors), ...dirMapPins(showFilter === 'orella' ? [] : dirRows)]}
                loading={dirLoading && showFilter !== 'orella'} loadingLabel={t('common.loading')}
                center={area ? { lat: area.lat, lng: area.lng } : mapCenter}
                centerPrecise={area ? false : !!(mapCenter && mapCenter.precise)}
                youPoint={area ? (mapCenter ? { lat: mapCenter.lat, lng: mapCenter.lng } : null) : undefined}
                youPrecise={!!(mapCenter && mapCenter.precise)}
                onViewportChange={setAreaView}
                recenterKey={mapArea.recenterKey}
                selectedId={selectedMapDoc ? selectedMapDoc.id : (selectedDirNpi ? `npi-${selectedDirNpi}` : null)}
                onSelect={(d) => {
                  // s126 (TestFlight build 95, Dr. Rami: "Clicking the 3 only open 1 of them"): a pin for several
                  //   clinicians at one address opens that office's sheet (every clinician there + "Any clinician"),
                  //   exactly as on the Doctors tab (s123 board F). It used to open the nearest one's card only.
                  if (d && d.__directory && d.__count > 1 && Array.isArray(d.__officeRows)) {
                    const offices = groupOffices(d.__officeRows)
                    setSelectedMapDoc(null); setSelectedDirNpi(null)
                    setPracticeSheet({ name: (offices[0] && offices[0].practice) || '', offices })
                    return
                  }
                  if (d && d.__directory) { setSelectedMapDoc(null); setSelectedDirNpi(d.npi) }
                  else { setSelectedDirNpi(null); setSelectedMapDoc(d) }
                }}
                mapsKey={MAPS_KEY}
                orellaOf={(d) => !d.__directory}
                fitRadiusMiles={distanceFilter || 25}
                fitExact={!!distanceFilter}
              />
              {/* ★ s104 (TestFlight b70, Dr. Rami: "Clicking a non orella pin does not indicate who the
                  selected doctor is. Should open their listing when clicked."): the tapped pin's listing
                  opens over the map, with the same action as its row below. */}
              {(() => {
                const dirSel = selectedDirNpi ? dirRows.find((r) => r.npi === selectedDirNpi) : null
                if (dirSel) {
                  return (
                    <MapPinCard kind="directory" doc={dirSel} miles={dirSel.miles} t={t}
                      onClose={() => setSelectedDirNpi(null)}
                      onAction={() => sendToDirectory(dirSel)} />
                  )
                }
                if (selectedMapDoc && showFilter !== 'other') {
                  return (
                    <MapPinCard kind="orella" doc={selectedMapDoc} miles={distanceOf(selectedMapDoc)} t={t}
                      onClose={() => setSelectedMapDoc(null)}
                      onViewProfile={() => setFullProfileSheet({ doctorId: selectedMapDoc.id, mode: 'select' })}
                      onAction={() => { setSelectedDoctor(selectedMapDoc); setStep('confirm-doctor') }} />
                  )
                }
                return null
              })()}
              {/* ★ s101 Phase 4: the patient moved the map -> offer to search there; while an area
                  is searched -> one tap back to their own location. */}
              <MapAreaButtons mapArea={mapArea} cardOpen={!!(selectedDirNpi || selectedMapDoc)} t={t} />
            </div>
          )}
        </div>
        <DoctorFiltersSheet
          showFilters={showFilters} setShowFilters={setShowFilters}
          resetFilters={resetFilters}
          specialtyFilter={null} setSpecialtyFilter={() => {}}
          distanceFilter={distanceFilter} setDistanceFilter={setDistanceFilter}
          virtualActive={virtualActive} setVirtualActive={setVirtualActive}
          sortBy={sortBy} setSortBy={setSortBy}
          patientHasInsurance={patientHasInsurance}
          insuranceActive={insuranceActive} setInsuranceActive={setInsuranceActive}
          specialtyLocked
          lockedSpecialtyLabel={lockedSpecialtyLabel}
          lockedSpecialtyCaption={lockedSpecialtyCaption}
          specialtyChoices={specialtyChoices} specialtyChoiceId={activeChoice.id} setSpecialtyChoiceId={setSpecialtyChoiceId}
          showFilter={showFilter} setShowFilter={setShowFilter}
        />

        {/* Doctors List */}
        <div className="flex-1 px-4 overflow-auto pb-4" style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}>
          {/* v213.4 (C-A-181): Out-of-area banner — shown when activeZip is set
              but no doctors fall within 25mi. Common when patient is traveling. */}
          {!loadingDoctors && filteredDoctors.length > 0 && (activeZip || currentCoords) && (() => {
            const nearbyCount = filteredDoctors.filter(d => {
              const dist = distanceOf(d) // PROD-655
              return dist !== null && dist <= 25
            }).length
            if (nearbyCount > 0) return null
            // ★ s99 D476 / s101 Phase 4: no Orella doctor nearby. The directory's doctors are listed
            //   in their own section under the Orella list now; this says why the list starts far.
            return (
              <div className="mb-3 rounded-xl p-3 flex items-start gap-2 text-[12.5px]" data-testid="no-orella-nearby"
                   style={{ background: '#F5FBFF', border: '1px solid #A7D9FC', color: '#15354F' }}>
                <AlertTriangle size={14} className="flex-none mt-0.5" style={{ color: '#46A8F9' }} />
                <span>{t('share.noOrellaNearbyShort', { defaultValue: 'No doctors connected to Orella within {{n}} miles. Send your case to another doctor near you.', n: 25 })}</span>
              </div>
            )
          })()}
          {loadingDoctors ? (
            <div className="flex items-center justify-center py-12">
              <OrellaSpinner size={32} />
            </div>
          ) : filteredDoctors.length > 0 ? (
            <>
              {/* s101 Build 67: "Show" = all / on Orella / other doctors. */}
              {/* s106 (canvas "Orella Referrals Screens", choose a doctor): the patient's own PCP first on an
                  HMO-type plan, from Profile › Insurance. */}
              {pcpFirst && (
                <div className="rounded-2xl bg-white p-3.5 mb-4 flex flex-col gap-1.5" data-testid="share-pcp-card"
                     style={{ border: '1.5px solid #46A8F9' }}>
                  <span className="self-start text-[12px] font-bold rounded-full px-2.5 py-1" style={{ background: '#E9F8FF', color: '#15354F' }}>{t('referral.pcpChip', 'Your primary care doctor (from your profile)')}</span>
                  <p className="m-0 text-[16px] font-extrabold" style={{ color: '#15354F' }}>{myPcp.name}{myPcp.credential ? `, ${myPcp.credential}` : ''}</p>
                  {(myPcp.specialty || myPcp.address) && <p className="m-0 text-[13px]" style={{ color: '#4E6577' }}>{[myPcp.specialty, myPcp.address].filter(Boolean).join(' · ')}</p>}
                  {referralPlan && <p className="m-0 text-[13px] leading-snug" style={{ color: '#4E6577' }}>{t('referral.pcpHint', { defaultValue: 'Your {{plan}} plan usually starts with your primary care doctor. Change it in Profile › Insurance.', plan: referralPlan })}</p>}
                  <button type="button" onClick={choosePcp} data-cta="share-send-pcp"
                          className="mt-1 w-full py-3 rounded-[14px] text-[15px] font-extrabold text-white" style={{ background: '#15354F' }}>
                    {clinicianTo(myPcp)
                      ? t('doctors.sendCaseToClinician', { defaultValue: 'Send my case to {{clinician}}', clinician: clinicianTo(myPcp) })
                      : t('doctors.sendCaseToProvider', 'Send my case to this clinician')}
                  </button>
                </div>
              )}
              {showFilter !== 'other' && (<>
              <p className="text-[15px] font-extrabold mb-1 px-0.5" style={{ color: '#15354F' }} data-testid="share-orella-heading">
                {t('share.bookOnOrella', 'Book and manage directly on Orella')}
              </p>
              <div className="text-[10px] font-bold text-gray-400 tracking-wider uppercase mb-2 px-0.5">
                {(activeChoice.primary ? t('doctors.primaryCare', 'Primary care') : activeChoice.label) || t('share.specialists')} • {filteredDoctors.length} {t('share.available')}
              </div>
              <div className="space-y-3">
                {/* ★★ v242.103.0 — THE DIRECTORY'S CARD, and the row IS the choice.
                    Dr. Rami: "tap the row". The old card was a gradient panel with a
                    View Profile / Select button pair; this is the same DoctorCard the
                    Doctors screen renders, so the two screens cannot drift apart.
                    ★ The chevron is suppressed by passing onViewProfile: a chevron on a
                      row that COMMITS would promise navigation and deliver a decision.
                    ★★ Everything the old card showed that this one does not — years in
                       practice, languages, the Board Cert. badge — lives on the profile,
                       one tap away through the link. */}
                {filteredDoctors.map(doctor => (
                  <div key={doctor.id}>
                  <DoctorCard
                    doc={doctor}
                    insurance={profile?.insurance || {}}
                    isMyDoctor={false}
                    isSelected={selectedMapDoc?.id === doctor.id}
                    onTap={() => { setSelectedDoctor(doctor); setStep('confirm-doctor') }}
                    onViewProfile={(d) => setFullProfileSheet({ doctorId: d.id, mode: 'select' })}
                  />
                  {needsReferral(doctor) && (
                    <span className="inline-block mt-1.5 ml-1 text-[12px] font-bold rounded-full px-2.5 py-1" data-testid="share-referral-chip"
                          style={{ background: '#FEF3C7', color: '#92400E' }}>
                      {t('referral.chipNeeded', { defaultValue: 'Referral needed · {{plan}}', plan: referralPlan })}
                    </span>
                  )}
                  </div>
                ))}
              </div>
              </>)}

              {/* ★ s101 Phase 4 (Dr. Rami 2026-10-13): ONE search. The doctors not on Orella are
                  listed right here, under the Orella doctors, from the national directory; the
                  "Don't see your doctor?" detour to a second finder is gone. */}
              {showFilter !== 'orella' && (
              <div className={showFilter === 'other' ? '' : 'mt-5 pt-4 border-t border-gray-100'} data-testid="share-directory-section">
                <DirectoryNearby
                  variant="section"
                  currentCase={currentCase}
                  zip={area && area.zip ? area.zip : (activeZip || homeZip)}
                  origin={distanceOrigin}
                  point={area ? { lat: area.lat, lng: area.lng } : distanceOrigin}
                  radiusMiles={distanceFilter || 25}
                  q={searchQuery}
                  taxonomy={activeChoice.taxonomy}
                  onRows={setDirRows}
                onLoading={setDirLoading}
                  selectedNpi={selectedDirNpi}
                  t={t}
                  onPick={(provider) => sendToDirectory(provider)}
                />
              </div>
              )}
            </>
          ) : (
            <div className="py-2 space-y-4">
              {/* s99 D476: the directory's nearest doctors first, then the prep path. */}
              <DirectoryNearby
                variant="section"
                currentCase={currentCase}
                zip={area && area.zip ? area.zip : (activeZip || homeZip)}
                origin={distanceOrigin}
                point={area ? { lat: area.lat, lng: area.lng } : distanceOrigin}
                radiusMiles={distanceFilter || 25}
                q={searchQuery}
                taxonomy={activeChoice.taxonomy}
                onRows={setDirRows}
                onLoading={setDirLoading}
                selectedNpi={selectedDirNpi}
                t={t}
                onPick={(provider) => sendToDirectory(provider)}
              />
              {/* ★ s114 B1 (Dr. Rami 2026-10-22 build-80 bugs, TestFlight 12): on the share hub's "Need a Doctor?"
                  path (findDoctorRef) or a case that already has a code, the case is PREPARED - the prep block
                  (Prepare my case, the 3 steps, Invite) is not shown there: only the directory and Back. */}
              {!(findDoctorRef.current || currentCase?.shareCode || currentCase?.share_code) && (<>
              {/* v234.0.0: Single clear path — no upfront specialist search.
                  When no Orella specialist matches, funnel straight into
                  MD-agnostic prep. The doctor the patient is seeing is added
                  later on the Diagnose step (ExternalDoctorLookup) → send case. */}
              <button
                onClick={() => {
                  updateAndSaveCase({ doctorSource: 'external', doctor_source: 'external', status: 'submitted' })
                  navigate('/case/detect') /* PROD-654: a push, back returns here */
                }}
                className="w-full rounded-2xl p-4 flex items-center justify-center gap-2.5 active:scale-[0.99] transition-all breathe-navy"
                style={{ backgroundColor: '#15354F' }}
              >
                <span className="text-[15px] font-bold text-white">{t('share.prepareMyCase') || 'Prepare my case'}</span>
                <ChevronRight size={18} className="text-white" />
              </button>

              {/* 3-step preview — sets expectation, ends on "add your doctor on Diagnose" */}
              <div className="space-y-3 px-1 pt-1">
                {[
                  { n: '1', title: t('share.prepStep1Title') || 'Finish your case', sub: t('share.prepStep1Sub') || 'Describe your symptoms and add any photos or documents' },
                  { n: '2', title: t('share.prepStep2Title') || 'Get your visit-prep summary', sub: t('share.prepStep2Sub') || 'A clear packet you can share with any doctor' },
                  { n: '3', title: t('share.prepStep3Title') || "Add the doctor you're seeing", sub: t('share.prepStep3Sub') || 'Search for them on the Diagnose step, then send your case' },
                ].map((s) => (
                  <div key={s.n} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[13px] font-extrabold"
                      style={{ backgroundColor: '#E9F8FF', border: '1px solid #A7D9FC', color: '#46A8F9' }}>
                      {s.n}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13.5px] font-bold" style={{ color: '#15354F' }}>{s.title}</p>
                      <p className="text-[12px] text-gray-400 leading-snug mt-0.5">{s.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Tertiary: Invite link — quiet footer */}
              <div className="text-center pt-1 pb-1">
                <p className="text-[11px] text-gray-400 mb-1">{t('share.knowADoctor') || 'Know a doctor who should be on Orella?'}</p>
                <button
                  onClick={() => {
                    const subject = 'Join Orella Health'
                    const body = `Hi,\n\nI'd like to share my health case with you through Orella Health, a platform that streamlines patient intake and communication.\n\nClinicians can join Orella at: ${signupUrl()}\n\nThank you!`
                    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
                  }}
                  className="text-[12px] font-semibold text-[#46A8F9] active:underline"
                >
                  {t('share.inviteThemToJoin') || 'Invite them to join'}
                </button>
              </div>
              </>)}
            </div>
          )}
        </div>

        {error && (
          <div className="px-4 pb-4">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-sm text-amber-700">{error}</p>
            </div>
          </div>
        )}
        <div className="p-4 pb-5 flex-shrink-0 bg-white border-t border-gray-100" style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}>
          <button onClick={() => navigate('/case/home')} className="w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] text-[#15354F] text-[14px] font-semibold flex items-center justify-center gap-1.5">{t('share.backToCaseHome')}</button>
        </div>

      </div>
      {renderProfileSheet()}
      {renderLocationSheet()}
      {renderOfficePicker()}
      {/* s126: the office chosen on the sheet IS the office (no second office question). */}
      {practiceSheet && <PracticeSendSheet practice={practiceSheet} t={t} onClose={() => setPracticeSheet(null)}
        onChoose={(target) => { setPracticeSheet(null); sendToDirectory({ ...target, officeChosen: true }) }} />}
      </>
    )
  }

  // ================== STEP: Confirm Doctor (Conversational Flow) ==================
  // s106 A2 — a const, not an `if (step === …)`: check-no-invented-slots R25 finds the confirm step by the
  //   FIRST if on that test, and this branch does not render ShareConversation.
  const showReferralNeeded = step === 'confirm-doctor' && !!selectedDoctor && !mdOnboardMode && !referralChoice && needsReferral(selectedDoctor)
  if (showReferralNeeded) {
    return (
      <div className="min-h-full bg-gray-50 flex flex-col">
        <CompactHeader stepNum={3} totalSteps={3} stepLabel={t('referral.neededTitle', 'Referral needed')} caseInfo={caseInfo} onBack={() => setStep('find-doctor')} onNotification={() => navigate('/notifications')} />
        <ReferralNeeded t={t} plan={referralPlan} pcp={myPcp}
          doctorName={clinicianName(selectedDoctor)}
          onSeePcp={() => { setReferralChoice('pcp'); choosePcp() }}
          onAsk={() => setReferralChoice('ask')}
          onUpload={() => setReferralChoice('upload')} />
      </div>
    )
  }

  if (step === 'confirm-doctor' && selectedDoctor) {
    return (
      <>
      <div className={`${onboardAppointmentNotSaved ? 'tabbar-screen-min-h' : 'min-h-full'} bg-gray-50 flex flex-col relative ${submitPhase ? 'h-full overflow-hidden' : ''}`}>
        <ChatKeyframes />
        <CompactHeader stepNum={3} totalSteps={3} stepLabel="Share" caseInfo={caseInfo} onBack={() => mdOnboardMode ? navigate('/cases') : setStep('find-doctor')} onNotification={() => navigate('/notifications')} />

        {/* Profile Requirements - Show at top so user sees it first */}
        {!requiredComplete && (
          <div className="px-4 pt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <AlertTriangle size={16} />
                {t('share.completeProfileFirst', 'Complete Your Profile First')}
              </h4>
              <p className="text-sm text-amber-700 mb-3">
                {t('share.completeProfileFirstBody', 'Your doctor needs this information to provide safe care. Please complete your profile before continuing.')}
              </p>
              <div className="space-y-2">
                {[
                  { key: 'allergies', complete: allergiesComplete, label: t('profile.allergies') },
                  { key: 'medications', complete: medicationsComplete, label: t('profile.medications') },
                  { key: 'identification', complete: identificationComplete, label: t('profile.identification') },
                  { key: 'insurance', complete: insuranceComplete, label: t('profile.insurance') },
                ].filter(item => !item.complete).map(item => (
                  <div key={item.key} className="flex items-center gap-2 text-sm text-amber-600">
                    <AlertTriangle size={14} />
                    <span>{t('share.itemRequired', { label: item.label })}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => { saveFormStateToSession(); navigate(`/profile?returnToShare=true&caseId=${currentCase?.id}${mdOnboardMode ? '&mdOnboard=true' : ''}`); }}
                className="mt-3 w-full bg-amber-600 text-white py-3 rounded-full font-semibold"
              >
                {t('share.completeProfileNow')}
              </button>
            </div>
          </div>
        )}

        {/* P1 round 5 (M4): the case was sent, the appointment was not saved */}
        {onboardAppointmentNotSaved && (
          <div className="flex-1 flex flex-col" data-onboard-appointment-not-saved>
            <div className="px-4 pt-4 pb-6">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl mb-3">
                <p className="text-sm font-semibold text-amber-800">{t('appointment.onboardSentAppointmentNotSaved')}</p>
              </div>
            </div>
            {/* PROD-1194 part C: Continue pinned on top of the TabBar */}
            <BottomActionBar aboveTabBar className="[--bar-px:16px]">
              <button onClick={() => navigate(currentCase?.describeComplete ? '/case/home' : '/case/describe/voice', { replace: true })}
                className="w-full py-3 rounded-full text-white text-[14px] font-bold border-none cursor-pointer"
                style={{ backgroundColor: NAVY }}>
                {t('common.continue', 'Continue')}
              </button>
            </BottomActionBar>
          </div>
        )}

        {/* Conversational Flow */}
        {requiredComplete && !onboardAppointmentNotSaved && (
          <div className="px-4 pt-4 flex-1 pb-6">
            {/* v222.0.0 Stedi — Eligibility badge for the selected doctor (compact) */}
            {(eligibility || eligibilityChecking) && (
              <div className="mb-3">
                {/* PROD-936 PAT-2: the server may answer this doctor's check with the stored result of
                    up to 30 days ago (reused: true), so the compact badge says how old it is. */}
                <EligibilityBadge
                  eligibility={eligibility}
                  variant="compact"
                  doctorRole={roleFromSpecialty(selectedDoctor?.specialties?.[0] || selectedDoctor?.specialty)}
                  loading={eligibilityChecking}
                  showAge
                />
                {!eligibilityChecking && <NetworkDisclaimer className="mt-1.5 px-1" />}
              </div>
            )}
            <ShareConversation
              doctor={selectedDoctor}
              currentCase={currentCase}
              availableSlots={availableSlots}
              slotPlaces={slotPlaces}
              loadingSlots={loadingSlots}
              slotsReason={slotsReason}
              onRetrySlots={() => setSlotsAttempt((n) => n + 1)}
              savedPharmacy={selectedPharmacy}
              agreementSigned={agreementSigned}
              signature={signature}
              onRequestAppointment={(data) => {
                if (data) {
                  // P1 round 6 (A1): a new pick answers the refusal shown above it
                  setError('')
                  setRequestAppointment(true)
                  setAppointmentType(data.type || 'video')
                  setSelectedDate(data.date)
                  setSelectedTime(data.time)
                } else {
                  setRequestAppointment(false)
                  setSelectedDate(null)
                  setSelectedTime(null)
                }
              }}
              onSelectPharmacy={(data) => setSelectedPharmacy(data)}
              onSignAgreement={() => setShowAgreementModal(true)}
              onSend={mdOnboardMode ? handleMDOnboardComplete : handleSendToDoctor}
              holdBooking={referralHold}
              onBack={() => setStep('find-doctor')}
              onViewProfile={() => setFullProfileSheet({ doctorId: selectedDoctor.id, mode: 'select' })}
              onStepChange={setConversationStep}
              sendingToDoctor={sendingToDoctor}
              mdOnboardMode={mdOnboardMode}
              existingAppointment={mdOnboardMode ? (mdOnboardAppointment || { date: selectedDate, time: selectedTime }) : null}
              authRequired={authRequired}
              // P1 round 6 (A1): the refusal both sends set (doctor_has_no_hours,
              // requested_slot_required, appointment_not_created, "just booked") was rendered
              // nowhere on this step; ShareConversation shows it where the patient is.
              error={error}
              slotConflictSeq={slotConflictSeq}
            />
          </div>
        )}

        {/* Submit Transition Overlay */}
        {submitPhase && (
          <>
            <style>{`
              @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }
              @keyframes overlayExit { from { transform: translateY(0); } to { transform: translateY(-100%); } }
              @keyframes iconPulse { 0%, 100% { transform: scale(1); opacity: 0.85; } 50% { transform: scale(1.06); opacity: 1; } }
              @keyframes ringExpand { 0%, 100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.25); opacity: 0; } }
              @keyframes dotBounce { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }
              @keyframes checkPop { 0% { transform: scale(0); opacity: 0; } 60% { transform: scale(1.2); } 100% { transform: scale(1); opacity: 1; } }
            `}</style>
            <OverlayPortal>
            <div 
              className="fixed inset-0 z-[100] flex flex-col items-center justify-center"
              style={{ backgroundColor: NAVY, animation: submitPhase === 'exit' ? 'overlayExit 0.5s ease-in forwards' : 'overlayIn 0.3s ease-out' }}
            >
              {submitPhase === 'sending' && (
                <>
                  <div className="relative mb-6">
                    <div className="absolute -inset-3 rounded-3xl border-2" style={{ borderColor: 'rgba(70,168,249,0.3)', animation: 'ringExpand 1.5s ease-in-out infinite' }} />
                    <div className="w-20 h-20 rounded-[22px] flex items-center justify-center" style={{ background: 'rgba(70,168,249,0.15)', animation: 'iconPulse 1.5s ease-in-out infinite' }}>
                      <Send size={32} className="text-[#46A8F9]" />
                    </div>
                  </div>
                  <p className="text-white font-bold text-[17px] mb-1.5">{t('share.sharingYourCase')}</p>
                  <p className="text-white/40 text-[13px]">{t('share.sendingToDr', 'Sending to Dr.')} {selectedDoctor?.last_name}</p>
                  <div className="flex gap-1.5 mt-5">
                    {[0, 0.2, 0.4].map((delay, i) => (
                      <span key={i} className="w-1.5 h-1.5 rounded-full" style={{ background: 'rgba(70,168,249,0.4)', animation: `dotBounce 1.2s ease-in-out infinite ${delay}s` }} />
                    ))}
                  </div>
                </>
              )}
              {(submitPhase === 'success' || submitPhase === 'exit') && (
                <>
                  <div className="w-20 h-20 rounded-[22px] flex items-center justify-center mb-6" style={{ background: 'rgba(102,209,112,0.2)' }}>
                    <Check size={40} strokeWidth={2.5} className="text-green-400" style={submitPhase === 'success' ? { animation: 'checkPop 0.4s ease-out' } : undefined} />
                  </div>
                  <p className="text-green-400 font-bold text-[17px] mb-1.5">{t('share.caseShared')}</p>
                  <p className="text-white/40 text-[13px]">{clinicianName(selectedDoctor || {}, 'short')} will review your case</p>
                </>
              )}
            </div>
            </OverlayPortal>
          </>
        )}
        
        {renderLocationSheet()}
        
        {/* Agreement Signing Modal */}
        {showAgreementModal && createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800">{t('share.signPatientCareAgreement')}</h3>
                <button onClick={() => setShowAgreementModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <AgreementChecklist practice={practiceTemplate} agreeOrella={agreeOrella} setAgreeOrella={setAgreeOrella}
                  agreePractice={agreePractice} setAgreePractice={setAgreePractice}
                  onReadOrella={() => setShowAgreementPreview(true)} onReadPractice={() => setShowPracticeView(true)}
                  doctorName={selectedDoctor ? clinicianName(selectedDoctor, 'short') : ''} t={t} />
                <PracticeAgreementModal isOpen={showPracticeView} onClose={() => setShowPracticeView(false)} agreement={practiceTemplate} t={t} />
                <div className="mb-4 p-3 bg-orella-50 rounded-lg" hidden={!!practiceTemplate}>
                  <p className="text-sm text-orella-700">
                    {t('share.signBelowLead')}{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); setShowAgreementPreview(true) }} className="text-orella-600 underline font-medium">{t('share.patientCareAgreement', 'Patient Care Agreement')}</a>
                    {' '}{t('share.signBelowTail')}
                  </p>
                </div>
                {/* ★★★ v242.87.0 (TF b34 #9) — "Patient share agreement is
                    highlighted as if it's a link but it's not."
                    The anchor above is real and does call
                    setShowAgreementPreview(true). What was missing is the
                    LISTENER: <AgreementPreviewModal> was rendered exactly once
                    in this 3,000-line file, inside the step==='generated'
                    early-return at the bottom. This block appears in the
                    'confirm-doctor' and 'initial' branches, which return long
                    before that line is reached — so the state flipped and
                    nothing mounted. A link that sets state no reader observes
                    is indistinguishable from a <span>.
                    The modal portals to document.body (AgreementPreviewModal.jsx
                    :132), so its position in the tree is irrelevant; only which
                    BRANCH renders it matters. ShareConversation.jsx:949 renders
                    its own copy in its single return, which is exactly why the
                    identical anchors there have always worked. */}
                <AgreementPreviewModal isOpen={showAgreementPreview} onClose={() => setShowAgreementPreview(false)} signatureData={agreementSigned ? (signature || '') : ''} doctor={selectedDoctor} />
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
                  <p className="text-xs text-gray-500 mb-2 text-center">{t('share.signBelowUsingYourFinger')}</p>
                  <canvas
                    ref={signatureCanvasRef}
                    width={350}
                    height={150}
                    className="w-full bg-white rounded border border-gray-200 touch-none"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <div className="flex gap-3 mt-4">
                  <button onClick={clearSignature} className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium">{t('share.clear')}</button>
                  <button onClick={saveSignature} disabled={!canAcceptSignature({ hasInk, practice: practiceTemplate, agreeOrella, agreePractice })} data-cta="share-sign-accept" className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium text-white ${canAcceptSignature({ hasInk, practice: practiceTemplate, agreeOrella, agreePractice }) ? 'bg-orella-500' : 'bg-gray-300'}`} style={{ cursor: canAcceptSignature({ hasInk, practice: practiceTemplate, agreeOrella, agreePractice }) ? 'pointer' : 'not-allowed' }}>{t('share.signAccept')}</button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
      {renderProfileSheet()}
      {consentSheet}
      </>
    )
  }

  // ================== STEP: Doctor Sent Success ==================
  // Handle cancel send to doctor
  const handleCancelSendToDoctor = async () => {
    if (!currentCase?.id) {
      console.error('[Cancel] No current case ID')
      setError(t('errors2.noCaseToCancel'))
      return
    }
    
    console.log('[Cancel] Cancelling send to doctor for case:', currentCase.id, 'status:', currentCase?.status)
    
    setCancellingShare(true)
    setError('')
    
    try {
      // v154.2.0: Use the proper revoke-share endpoint which:
      // 1. Clears share code, target doctor, appointment request on the case
      // 2. Cancels all pending/scheduled/confirmed appointments in the appointments table
      // 3. Sets case status back to 'submitted'
      const result = await casesAPI.revokeShareCode(currentCase.id)
      
      console.log('[Cancel] Revoke share result:', !!result?.success) // PROD-903: the answer carries the revoked share code
      
      // v199.2: Use updateAndSaveCase instead of updateCase to ensure
      // cleared fields persist properly and don't get overwritten by
      // the store's smart-merge logic (which preserves truthy values)
      updateAndSaveCase({
        sentToDoctor: null,
        sentToDoctorName: null,
        sentToDoctorAt: null,
        sentToDoctorData: null,
        appointmentRequest: null,
        appointments: [],
        scheduledAppointment: null,
        followUpAppointment: null,
        appointment: null,
        appointmentStatus: null,
        shareCode: null,
        share_code: null,
        sharePin: null,
        targetDoctorId: null,
        target_doctor_id: null,
        doctor: null,
        doctor_id: null,
        doctorSource: null,
        doctor_source: null,
        isShared: false,
        detectComplete: false,
        status: 'ready_to_share'
      })
      
      // Reset local UI state
      setSelectedDoctor(null)
      setSelectedDate(null)
      setSelectedTime(null)
      setRequestAppointment(true)
      
      // Go back to find doctor step (not choose-method, since they want to pick a different doctor)
      setStep('find-doctor')
    } catch (err) {
      console.error('[Cancel] Failed to cancel send:', err)
      setError(t('errors.failedToCancel', { message: err.message || t('common.pleaseTryAgain') }))
    } finally {
      setCancellingShare(false)
    }
  }

  // PROD-285: the confirmed "Revoke Access". Same shape as handleCancelSendToDoctor:
  // the local share is cleared ONLY after the server calls succeed. On a refusal the
  // share stays as it is and the patient is told, instead of the old silent clear that
  // said the doctor had lost access while the code still opened the case.
  // ★★ PROD-424 (a), patient push 3a: the message is the SERVER's (apiFetch puts it in .error,
  //   not .message, so every refusal used to read "Please try again", even the 400 "cannot
  //   withdraw a completed case"), and a revoke that succeeded before the status update failed
  //   says the code is already dead instead of "Failed to revoke access".
  const handleRevokeAccess = async () => {
    setRevokingAccess(true)
    setError('')
    let revoked = false
    try {
      if (shareCode) {
        await casesAPI.revokeShareCode(currentCase.id)
        revoked = true
      }
      await casesAPI.update(currentCase.id, { status: 'ready_to_share' })
      await updateAndSaveCase({ shareCode: null, isShared: false, status: 'ready_to_share' })
      setShowRevokeConfirm(false)
      setStep('find-doctor')
      setShareCode('')
      setPin('')
    } catch (err) {
      console.warn('[ShareScreen] Revoke failed:', err)
      setShowRevokeConfirm(false)
      const message = apiErrorMessage(err, t('common.pleaseTryAgain'))
      setError(revoked ? t('errors.revokedNotSaved', { message }) : t('errors.failedToRevoke', { message }))
    } finally {
      setRevokingAccess(false)
    }
  }

  if (step === 'doctor-sent') {
    const appointmentReq = currentCase?.appointmentRequest
    const docName = selectedDoctor ? clinicianName(selectedDoctor) : currentCase?.sentToDoctorName
    const docInitials = `${selectedDoctor?.first_name?.[0] || currentCase?.sentToDoctorName?.[4] || ''}${selectedDoctor?.last_name?.[0] || currentCase?.sentToDoctorName?.split(' ').pop()?.[0] || ''}`
    return (
      <div className="tabbar-screen-min-h bg-white flex flex-col">
        <ChatKeyframes />
        <CompactHeader stepNum={3} totalSteps={3} stepLabel="Share" caseInfo={caseInfo} onBack={() => navigate('/case/home')} onNotification={() => navigate('/notifications')} />
        <OrellaChatBubble
          message={t('shareConv.caseSharedSuccess', { doctor: docName }) || `Your case has been shared successfully with ${docName}. See details below to confirm.`}
          animationKey={`share-sent-${currentCase?.id}`}
          onComplete={() => setShowContent(true)}
        />
        <div ref={doctorSentScrollRef} className="flex-1 px-4 pt-2 pb-4 overflow-auto" style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}>

          {/* ── Doctor card — matches doctor-select card style ── */}
          <div className="rounded-2xl mb-3 overflow-hidden" style={{ background: '#E9F8FF', border: '1.5px solid #A7D9FC' }}>
            <div className="flex items-center gap-3 p-3.5 pb-2">
              <div className="w-11 h-11 bg-white rounded-full flex items-center justify-center overflow-hidden flex-shrink-0" style={{ border: '2px solid rgba(167,217,252,0.3)' }}>
                {(selectedDoctor?.profile_photo || currentCase?.doctor?.profile_photo) ? (
                  <img src={selectedDoctor?.profile_photo || currentCase?.doctor?.profile_photo} alt="" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-[#46A8F9] font-bold text-[13px]">{docInitials}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-[14px] truncate" style={{ color: '#15354F' }}>{docName}</p>
                </div>
                <p className="text-[#46A8F9] text-[12px] font-medium">{formatProviderSpecialty(selectedDoctor?.matchedSpecialty || selectedDoctor?.specialties?.[0] || selectedDoctor?.specialty || 'Specialist', t)}</p>
                {(selectedDoctor?.practice_name || selectedDoctor?.practice_city) && (
                  <p className="text-[11px] text-gray-400 mt-0.5 truncate">
                    {selectedDoctor?.practice_name || ''}{selectedDoctor?.practice_name && selectedDoctor?.practice_city ? ' · ' : ''}{selectedDoctor?.practice_city}{selectedDoctor?.practice_state ? `, ${selectedDoctor.practice_state}` : ''}
                  </p>
                )}
              </div>
            </div>
          </div>
          
          {/* ── Appointment card — white with LIGHT_BLUE border ── */}
          {(appointmentReq || (requestAppointment && selectedDate && selectedTime)) && (
            <div className="bg-white rounded-2xl p-4 mb-3" style={{ border: '1.5px solid #A7D9FC' }}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="text-[#46A8F9]" size={15} />
                <span className="font-bold text-[#15354F] text-[12px]">{t('share.appointmentRequested')}</span>
              </div>
              <p className="text-[13px] text-[#15354F] font-semibold">
                {formatDate(appointmentReq?.requestedDate || selectedDate, { weekday: 'long', month: 'long', day: 'numeric' })} at{' '}
                {formatAppointmentTime(appointmentReq?.requestedTime || selectedTime)}
              </p>
              <p className="text-[10px] mt-1 flex items-center gap-1" style={{ color: '#94A3B8' }}>
                <Clock size={10} style={{ color: '#A7D9FC' }} />
                {(appointmentReq?.appointmentType || appointmentType) === 'video' ? 'Virtual visit' : 'In-person visit'} · Pending confirmation
              </p>
              {currentCase?.status !== 'claimed' && (
                <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(167,217,252,0.3)' }}>
                  <button
                    onClick={handleCancelSendToDoctor}
                    disabled={cancellingShare}
                    className="inline-flex items-center gap-1.5 text-red-400 text-[11px] font-medium disabled:opacity-50"
                  >
                    {cancellingShare ? <><OrellaSpinner size={16} tone="inherit" /> {t('share.cancelling', 'Cancelling...')}</> : <><X size={12} /> {t('share.cancelAndSendToDifferentDoctor', 'Cancel and send to different doctor')}</>}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Cancel option — fallback if no appointment card shown */}
          {!(appointmentReq || (requestAppointment && selectedDate && selectedTime)) && currentCase?.status !== 'claimed' && (
            <div className="text-center mt-2 mb-3">
              <button
                onClick={handleCancelSendToDoctor}
                disabled={cancellingShare}
                className="inline-flex items-center gap-1.5 text-red-400 text-[12px] font-medium disabled:opacity-50"
              >
                {cancellingShare ? <><OrellaSpinner size={16} tone="inherit" /> {t('share.cancelling', 'Cancelling...')}</> : <><X size={12} /> {t('share.cancelAndSendToDifferentDoctor', 'Cancel and send to different doctor')}</>}
              </button>
            </div>
          )}

          {/* v222.0.0 Stedi — Verified coverage card (Seam 1 visible) */}
          {/* v222.4.11 — Re-scan / Edit member ID CTAs route to profile insurance */}
          {(eligibility || eligibilityChecking) && (
            <div className="mb-3">
              <EligibilityBadge
                eligibility={eligibility}
                variant="detailed"
                doctorRole={roleFromSpecialty(selectedDoctor?.specialties?.[0] || selectedDoctor?.specialty)}
                loading={eligibilityChecking}
                onRescanCard={() => navigate('/profile/insurance?action=scan')}
                onEditMemberId={() => navigate('/profile/insurance?focus=memberId')}
              />
            </div>
          )}

          {/* ── What happens next — connected vertical stepper (v225.29.0).
               ★ s117 (TestFlight b84, Dr. Rami 2026-10-24: "make this larger and mention that doctor is now reviewing case
               ahead of the appointment. Do not link to detect as it has already been completed."): larger type; step 2 is
               the doctor reviewing the case (current), step 3 the visit; "Add clinical details" is gone. ── */}
          <div className="rounded-2xl bg-white p-5 pb-6" style={{ border: '1px solid #E8F1F8', boxShadow: '0 1px 3px rgba(21,53,79,0.04)' }}>
            <p className="text-[13px] font-bold uppercase tracking-[1.5px] mb-5" style={{ color: '#5F7A8E' }}>{t('share.whatHappensNext', 'What happens next')}</p>
            <div className="relative">
              <div className="absolute" style={{ left: 17, top: 18, bottom: 18, width: 2, background: 'linear-gradient(#66D170 0%, #66D170 30%, #A7D9FC 55%, #E2E8F0 100%)' }} />

              {/* Step 1 — done */}
              <div className="relative flex gap-4 pb-6">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 relative z-10" style={{ background: '#66D170', boxShadow: '0 0 0 4px #fff' }}>
                  <Check size={18} className="text-white" strokeWidth={3} />
                </div>
                <div className="pt-1">
                  <p className="text-[17px] font-semibold" style={{ color: '#15354F' }}>{t('share.caseSharedWithDoctor', 'Case shared with doctor')}</p>
                  <p className="text-[14px] mt-0.5" style={{ color: '#5F7A8E' }}>{t('share.justNow', 'Just now')}</p>
                </div>
              </div>

              {/* Step 2 — current: the doctor reviews the case ahead of the appointment */}
              <div className="relative flex gap-4 pb-6">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 relative z-10" style={{ background: '#46A8F9', boxShadow: '0 0 0 4px #fff, 0 0 0 7px #E9F8FF' }}>
                  <span className="text-white text-[15px] font-bold">2</span>
                </div>
                <div className="flex-1 rounded-[14px] px-3.5 py-3 -mt-0.5" style={{ background: '#F4FAFF', border: '1px solid #DCEEFF' }}>
                  <p className="text-[17px] font-bold" style={{ color: '#15354F' }}>{t('share.doctorReviewingCase', 'Doctor reviewing your case')}</p>
                  <p className="text-[14px] mt-1 leading-snug" style={{ color: '#3E5468' }}>{t('share.doctorReviewingAhead', 'Your doctor is now reviewing your case ahead of your appointment.')}</p>
                </div>
              </div>

              {/* Step 3 — upcoming: the visit */}
              <div className="relative flex gap-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 relative z-10 bg-white" style={{ border: '2px solid #CFE6FA', boxShadow: '0 0 0 4px #fff' }}>
                  <span className="text-[15px] font-bold" style={{ color: '#7FB9E8' }}>3</span>
                </div>
                <div className="pt-1">
                  <p className="text-[17px] font-semibold" style={{ color: '#5F7A8E' }}>{t('share.yourVisitStep', 'Your visit')}</p>
                  <p className="text-[14px] mt-0.5" style={{ color: '#7A8B9A' }}>{t('share.notifiedWhenConfirmed', "You'll be notified when your appointment is confirmed.")}</p>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-3">
              <p className="text-[12px] text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* PROD-1194 part C: pinned on top of the TabBar. */}
        <BottomActionBar aboveTabBar className="[--bar-px:16px]" style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}>
          {/* ★★ s101 (Dr. Rami, 2026-10-12): "the detect screen is shown to everyone before the share.
              So after the user completes the orella direct share sequence the next screen should be the
              diagnose confirmation screen, not detect which was already completed earlier in the sequence.
              On case home they can go to detect to View/Add as necessary." Detect only when it is NOT done. */}
          {/* ★ s117 (TestFlight b84): always on to the doctor screen (Diagnose, "Your case is being reviewed") — Detect was
              done before the share; Case Home still opens it to View/Add. */}
          <button data-sent-next="diagnose" onClick={() => navigate('/case/diagnose') /* PROD-654: a push, back returns here */} className="w-full py-3.5 rounded-full font-bold text-[15px] text-white flex items-center justify-center gap-2 breathe-blue" style={{ backgroundColor: '#46A8F9' }}>
            {t('common.continue') || 'Continue'}
            <ArrowRight size={18} strokeWidth={2.5} />
          </button>
          <button onClick={() => navigate('/case/home')} className="w-full py-2.5 text-[14px] font-semibold text-gray-400 mt-1 flex items-center justify-center">{t('share.backToCaseHome')}</button>
        </BottomActionBar>
      </div>
    )
  }

  // ================== STEP: Initial (Code Generation) ==================
  if (step === 'initial') {
    // Check if ready to generate code (profile complete, pharmacy selected, agreement signed)
    const canGenerateCode = (requiredComplete || profileSkipped) && selectedPharmacy && agreementSigned
    
    return (
      <div className="tabbar-screen-min-h bg-white flex flex-col">
        <ChatKeyframes />
        <CompactHeader stepNum={3} totalSteps={3} stepLabel="Share Code" caseInfo={caseInfo} onBack={() => setStep('find-doctor')} onNotification={() => navigate('/notifications')} />
        <OrellaChatBubble
          message={t('share.chatGenerateCode') || "I'll generate a secure code you can share with any clinician. They'll use it on the Orella MD panel to pull up your case."}
          animationKey={`share-code-${currentCase?.id}`}
          onComplete={() => setShowContent(true)}
        />

        <div className="flex-1 px-4 pt-1 pb-4 overflow-auto" style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}>
          {/* Profile Completion Card */}
          <div className="bg-[#E9F8FF] rounded-2xl p-4 mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-[11px] font-bold text-[#15354F]">{t('share.profileLabel', 'Profile:')} {completionPercent}%</span>
              {requiredComplete ? <span className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-green-100 text-green-700">{t('share.complete')}</span> : <span className="text-[9px] font-bold px-2 py-0.5 rounded-md bg-amber-200 text-amber-700">{t('share.incomplete')}</span>}
            </div>
            <div className="h-1.5 rounded-full bg-white overflow-hidden mb-2">
              <div className="h-full rounded-full bg-gradient-to-r from-[#46A8F9] to-[#15354F] transition-all" style={{ width: `${completionPercent}%` }} />
            </div>
            
            <div className="space-y-1.5">
              {[
                { key: 'allergies', complete: allergiesComplete, label: t('profile.allergies') },
                { key: 'medications', complete: medicationsComplete, label: t('profile.medications') },
                { key: 'identification', complete: identificationComplete, label: t('profile.identification') },
                { key: 'insurance', complete: insuranceComplete, label: t('profile.insurance') },
              ].map(item => (
                <div key={item.key} className="flex items-center gap-2">
                  {item.complete ? <Check className="text-green-500" size={14} strokeWidth={3} /> : <AlertTriangle className="text-amber-500" size={14} />}
                  <span className={`text-[11px] font-medium ${item.complete ? 'text-gray-500' : 'text-amber-700'}`}>
                    {item.complete ? t('share.itemComplete', { label: item.label }) : t('share.itemRequired', { label: item.label })}
                  </span>
                </div>
              ))}
            </div>
            
            {!requiredComplete && (
              <div className="flex gap-2 mt-3">
                <button 
                  onClick={() => { saveFormStateToSession(); navigate(`/profile?returnToShare=true&caseId=${currentCase?.id}${mdOnboardMode ? '&mdOnboard=true' : ''}`); }}
                  className="px-4 py-2 bg-[#46A8F9] text-white rounded-full text-[12px] font-bold"
                >
                  {t('share.completeProfileCta')}
                </button>
                <button 
                  onClick={() => setShowSkipWarning(true)}
                  className="px-4 py-2 border border-gray-300 text-gray-500 rounded-full text-[12px] font-semibold"
                >
                  {t('common.skip')}
                </button>
              </div>
            )}
          </div>

          {/* Pharmacy Selection Section */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-3">
            <div className="p-3 flex items-center gap-3 border-b border-gray-100">
              <Pill className="text-green-500" size={16} />
              <span className="font-bold text-[12px] text-gray-800">{t('share.selectPreferredPharmacy')}</span>
              {selectedPharmacy && <Check size={14} className="text-green-500 ml-auto" />}
            </div>
            <div className="p-3">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input type="text" value={pharmacySearchQuery}
                  onChange={(e) => { setPharmacySearchQuery(e.target.value); searchPharmacies(e.target.value); }}
                  placeholder={t('share.searchPharmacy')}
                  className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-[12px] focus:outline-none focus:border-orella-500"
                />
                {searchingPharmacy && <OrellaSpinner size={16} className="absolute right-3 top-1/2 -translate-y-1/2" />}
              </div>
              {selectedPharmacy && (
                <div className="mt-2 p-2.5 bg-green-50 border border-green-200 rounded-lg flex items-start justify-between">
                  <div>
                    <p className="font-medium text-green-800 text-[12px]">{selectedPharmacy.name}</p>
                    <p className="text-[11px] text-green-700">{selectedPharmacy.address}, {selectedPharmacy.city}, {selectedPharmacy.state}</p>
                  </div>
                  <button onClick={() => setSelectedPharmacy(null)} className="text-green-600"><X size={16} /></button>
                </div>
              )}
              {pharmacyResults.length > 0 && !selectedPharmacy && (
                <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden max-h-36 overflow-y-auto">
                  {pharmacyResults.map(pharmacy => (
                    <button key={pharmacy.id} onClick={() => { setSelectedPharmacy(pharmacy); setPharmacySearchQuery(''); setPharmacyResults([]); }}
                      className="w-full p-2.5 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0">
                      <p className="font-medium text-gray-800 text-[12px]">{pharmacy.name}</p>
                      <p className="text-[11px] text-gray-600">{pharmacy.address}, {pharmacy.city}, {pharmacy.state} {pharmacy.zip}</p>
                    </button>
                  ))}
                </div>
              )}
              {pharmacySearchQuery.length >= 2 && pharmacyResults.length === 0 && !searchingPharmacy && (
                <p className="mt-2 text-[11px] text-gray-500 text-center">{t('share.noPharmaciesFound')}</p>
              )}
            </div>
          </div>

          {/* Patient Care Agreement */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-3">
            <div className="p-3 flex items-center gap-3 border-b border-gray-100">
              <FileText className="text-orella-500" size={16} />
              <span className="font-bold text-[12px] text-gray-800">{t('share.patientCareAgreement')}</span>
              {agreementSigned && <Check size={14} className="text-green-500 ml-auto" />}
            </div>
            <div className="p-3">
              {!agreementSigned ? (
                <div className="flex gap-2">
                  <a href="#" onClick={(e) => { e.preventDefault(); setShowAgreementPreview(true) }}
                    className="flex-1 py-2 px-3 bg-gray-100 text-gray-700 rounded-lg text-[11px] font-medium flex items-center justify-center gap-1.5 hover:bg-gray-200">
                    <FileText size={14} /> {t('common.view', 'View')}
                  </a>
                  <button onClick={() => setShowAgreementModal(true)}
                    className="flex-1 py-2 px-3 bg-orella-500 text-white rounded-lg text-[11px] font-medium flex items-center justify-center gap-1.5">
                    <PenTool size={14} /> {t('share.signAgreement')}
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 p-2.5 bg-green-50 border border-green-200 rounded-lg">
                  <CheckCircle className="text-green-500" size={18} />
                  <div className="flex-1">
                    <p className="font-medium text-green-800 text-[12px]">{t('share.agreementSigned')}</p>
                    <p className="text-[10px] text-green-600">{t('share.signedOn', 'Signed on')} {formatDate(new Date().toISOString())}</p>
                  </div>
                  <button onClick={() => setShowAgreementModal(true)} className="text-green-600 text-[10px] underline">{t('share.resign')}</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer — PROD-1194 part C: pinned on top of the TabBar; the error is the bar's note. */}
        <BottomActionBar aboveTabBar className="[--bar-px:16px]" style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}
          note={error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-xl"><p className="text-[12px] text-red-600">{error}</p></div>}>
          <button
            onClick={() => canGenerateCode ? handleGenerateCode() : (!requiredComplete ? setShowSkipWarning(true) : null)}
            disabled={generating || !canGenerateCode}
            className={`w-full py-3 rounded-full font-bold text-[15px] flex items-center justify-center gap-2 ${canGenerateCode && !generating ? 'breathe-navy' : ''}`}
            style={{ backgroundColor: canGenerateCode ? NAVY : '#E2E8F0', color: canGenerateCode ? 'white' : '#94A3B8' }}
          >
            {generating ? <><OrellaSpinner size={18} tone="inherit" /> {t('share.generating', 'Generating...')}</> : <><Lock size={16} /> {t('share.generateShareCode', 'Generate Share Code')}</>}
          </button>
          <button onClick={() => setStep('find-doctor')} className="w-full text-[#46A8F9] py-2.5 text-[13px] font-semibold mt-1">{t('share.findAnOrellaDoctorInstead')}</button>
        </BottomActionBar>

        {/* Skip Warning Modal */}
        {showSkipWarning && createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center">
            <div className="absolute inset-0 bg-black/50" onClick={() => setShowSkipWarning(false)} />
            <div className="relative bg-white rounded-3xl p-6 mx-4 w-full max-w-sm">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="text-amber-500" size={32} />
                </div>
              </div>
              <h2 className="text-xl font-bold text-orella-700 text-center mb-2">{t('share.incompleteProfile')}</h2>
              <p className="text-gray-600 text-center text-sm mb-6">
                {t('share.incompleteProfileWarning', 'Without complete allergy and medication information, your doctor may have insufficient information to make the most accurate diagnosis.')}
              </p>
              <button onClick={() => { setShowSkipWarning(false); saveFormStateToSession(); navigate(`/profile?returnToShare=true&caseId=${currentCase?.id}${mdOnboardMode ? '&mdOnboard=true' : ''}`); }}
                className="w-full bg-orella-500 text-white py-3 rounded-full font-semibold mb-3">{t('share.completeProfile')}</button>
              <button onClick={() => setShowSkipWarning(false)}
                className="w-full border border-gray-300 text-gray-600 py-3 rounded-full font-semibold mb-2">{t('share.cancel')}</button>
              <button onClick={() => { setShowSkipWarning(false); setProfileSkipped(true); }}
                className="w-full text-gray-400 py-2 text-[12px] font-medium">{t('share.continueWithoutCompleting')}</button>
            </div>
          </div>,
          document.body
        )}
        
        {/* Agreement Signing Modal */}
        {showAgreementModal && createPortal(
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-800">{t('share.signPatientCareAgreement')}</h3>
                <button onClick={() => setShowAgreementModal(false)} className="text-gray-400 hover:text-gray-600"><X size={24} /></button>
              </div>
              <div className="p-4 overflow-y-auto flex-1">
                <AgreementChecklist practice={practiceTemplate} agreeOrella={agreeOrella} setAgreeOrella={setAgreeOrella}
                  agreePractice={agreePractice} setAgreePractice={setAgreePractice}
                  onReadOrella={() => setShowAgreementPreview(true)} onReadPractice={() => setShowPracticeView(true)}
                  doctorName={selectedDoctor ? clinicianName(selectedDoctor, 'short') : ''} t={t} />
                <PracticeAgreementModal isOpen={showPracticeView} onClose={() => setShowPracticeView(false)} agreement={practiceTemplate} t={t} />
                <div className="mb-4 p-3 bg-orella-50 rounded-lg" hidden={!!practiceTemplate}>
                  <p className="text-sm text-orella-700">
                    {t('share.signBelowLead')}{' '}
                    <a href="#" onClick={(e) => { e.preventDefault(); setShowAgreementPreview(true) }} className="text-orella-600 underline font-medium">{t('share.patientCareAgreement', 'Patient Care Agreement')}</a>
                    {' '}{t('share.signBelowTail')}
                  </p>
                </div>
                {/* ★★★ v242.87.0 (TF b34 #9) — "Patient share agreement is
                    highlighted as if it's a link but it's not."
                    The anchor above is real and does call
                    setShowAgreementPreview(true). What was missing is the
                    LISTENER: <AgreementPreviewModal> was rendered exactly once
                    in this 3,000-line file, inside the step==='generated'
                    early-return at the bottom. This block appears in the
                    'confirm-doctor' and 'initial' branches, which return long
                    before that line is reached — so the state flipped and
                    nothing mounted. A link that sets state no reader observes
                    is indistinguishable from a <span>.
                    The modal portals to document.body (AgreementPreviewModal.jsx
                    :132), so its position in the tree is irrelevant; only which
                    BRANCH renders it matters. ShareConversation.jsx:949 renders
                    its own copy in its single return, which is exactly why the
                    identical anchors there have always worked. */}
                <AgreementPreviewModal isOpen={showAgreementPreview} onClose={() => setShowAgreementPreview(false)} signatureData={agreementSigned ? (signature || '') : ''} doctor={selectedDoctor} />
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2">
                  <p className="text-xs text-gray-500 mb-2 text-center">{t('share.signBelowUsingYourFinger')}</p>
                  <canvas ref={signatureCanvasRef} width={350} height={150}
                    className="w-full bg-white rounded border border-gray-200 touch-none"
                    onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
                </div>
                <div className="flex gap-3 mt-4">
                  <button onClick={clearSignature} className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium">{t('share.clear')}</button>
                  <button onClick={saveSignature} disabled={!canAcceptSignature({ hasInk, practice: practiceTemplate, agreeOrella, agreePractice })} data-cta="share-sign-accept" className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium text-white ${canAcceptSignature({ hasInk, practice: practiceTemplate, agreeOrella, agreePractice }) ? 'bg-orella-500' : 'bg-gray-300'}`} style={{ cursor: canAcceptSignature({ hasInk, practice: practiceTemplate, agreeOrella, agreePractice }) ? 'pointer' : 'not-allowed' }}>{t('share.signAccept')}</button>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    )
  }

  // ================== STEP: Generated (Code Ready) ==================
  return (
    <div className="tabbar-screen-min-h bg-white flex flex-col">
      <ChatKeyframes />
      <CompactHeader stepNum={3} totalSteps={3} stepLabel="Share Code" caseInfo={caseInfo} onBack={() => caseBack(navigate, { skip: shareBackSkip })} onNotification={() => navigate('/notifications')} />
      <OrellaChatBubble
        message={t('share.chatCodeReady') || "Your share code is ready. Hand this to your doctor — they'll enter it on the Orella MD panel to pull up your case directly."}
        animationKey={`share-generated-${currentCase?.id}`}
        onComplete={() => setShowContent(true)}
      />

      <div className="flex-1 px-4 pt-1 pb-4 overflow-auto" style={{ transition: 'opacity 0.4s ease, transform 0.4s ease', opacity: showContent ? 1 : 0, transform: showContent ? 'translateY(0)' : 'translateY(12px)' }}>
        {/* Share Code Card */}
        <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-5 text-center mb-4">
          <div className="flex items-center justify-center gap-1.5 mb-2">
            <Lock size={12} className="text-gray-400" />
            <span className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">{t('share.secureShareCode')}</span>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-3xl font-mono font-extrabold text-[#15354F] tracking-[4px]">{shareCode}</span>
          </div>
          <p className="text-[10px] text-gray-400">{t('share.expiresLabel', 'Expires:')} {expirationDate}</p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mb-4">
          <button onClick={handleCopyCode} className="flex-1 bg-[#E9F8FF] border-[1.5px] border-[#A7D9FC] rounded-xl p-3 text-center">
            {copied ? <Check size={16} className="text-green-500 mx-auto mb-1" /> : <Copy size={16} className="text-[#46A8F9] mx-auto mb-1" />}
            <span className="text-[10px] font-bold text-[#46A8F9]">{copied ? 'Copied!' : 'Copy Code'}</span>
          </button>
          <button onClick={handleShareViaEmail} className="flex-1 bg-gray-50 border-[1.5px] border-gray-200 rounded-xl p-3 text-center">
            <Mail size={16} className="text-gray-400 mx-auto mb-1" />
            <span className="text-[10px] font-bold text-gray-400">{t('share.emailCode')}</span>
          </button>
          <button onClick={handleShareViaText} className="flex-1 bg-gray-50 border-[1.5px] border-gray-200 rounded-xl p-3 text-center">
            <MessageSquare size={16} className="text-gray-400 mx-auto mb-1" />
            <span className="text-[10px] font-bold text-gray-400">{t('share.textCode')}</span>
          </button>
        </div>

        {/* PIN info - shows a server-minted PIN only. Nothing in the app can set one; see the
            note on the options row below. */}
        {pin && (
          <div className="bg-amber-50 border-[1.5px] border-amber-200 rounded-xl p-3 flex items-start gap-2.5 mb-4">
            <Lock size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="text-[11px] font-bold text-amber-800">PIN Protected: <span className="font-mono">{pin}</span></div>
              <div className="text-[10px] text-amber-600 leading-snug mt-0.5">{t('share.yourDoctorWillAlsoNeed')}</div>
            </div>
          </div>
        )}

        {/* ★★★ THE "MORE OPTIONS" ROW IS GONE, AND BOTH DELETIONS ARE DELIBERATE.
            This merge (2026-09-11) met two removals of the SAME row, five days apart, and
            both carried the same author line -- which on this Mac says nothing, since every
            clone is configured user.name = drrami-ops (PROD-45). Attribute by commit, not by
            author. 736c934c removed the "Add PIN" button: it wrote the PIN through
            updateCase() - the local-only store setter - so it never reached the server and
            the case opened with the share code alone, while the UI told the patient "PIN
            Protected" (D72). a4b48d63, share remodel Wave 0, removed the Fax button. Each
            branch kept the button the other had deleted, so taking EITHER side whole would
            have resurrected a defect the OTHER side had already fixed.
            Server-side enforcement is unchanged (orella-api api/index.py: the Tier-0
            share-code preview gate and the claim route both check cases.share_pin), so a
            PIN the API mints still arrives as response.pin and the read-only banner above
            shows it. DO NOT RE-ADD A WAY TO TYPE A PIN HERE unless it is actually sent to
            the server. The row held nothing else, so it is removed and not left empty. */}

        {/* Invite your doctor */}
        <div className="text-center pt-3 border-t border-gray-100">
          <p className="text-[10px] text-gray-400">{t('share.wantYourDoctorOnOrella')}</p>
          <button onClick={() => {
            const subject = 'Join Orella Health'
            const body = `Hi,\n\nI'd like to share my health case with you through Orella Health, a platform that streamlines patient intake and communication.\n\nClinicians can join Orella at: ${signupUrl()}\n\nThank you!`
            window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
          }} className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#46A8F9] mt-1.5">
            <UserCheck size={12} /> {t('share.inviteDoctor')}
          </button>
        </div>
      </div>

      {/* Footer — PROD-1194 part C: "Continue to Detect" is pinned on top of the TabBar (the error is the
          bar's note); the two secondaries follow the bar and scroll (part-A rule: more than one). */}
      <BottomActionBar aboveTabBar className="[--bar-px:16px]" style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}
        note={error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-xl"><p className="text-[12px] text-red-600">{error}</p></div>}>
        <button onClick={() => navigate('/case/detect') /* PROD-654: a push, back returns here */} className="w-full py-3 rounded-full font-bold text-[15px] text-white" style={{ backgroundColor: '#46A8F9' }}>
          {t('share.continueToDetect', 'Continue to Detect')}
        </button>
      </BottomActionBar>
      <div className="px-4 pb-4 bg-white" data-footer-rest style={{ transition: 'opacity 0.4s ease', transitionDelay: '150ms', opacity: showContent ? 1 : 0 }}>
        <button onClick={() => navigate('/case/home')} className="w-full py-2.5 rounded-full border-[1.5px] border-[#D8E6F0] text-[#15354F] text-[14px] font-semibold mt-1 flex items-center justify-center gap-1.5">{t('share.backToCaseHome')}</button>
        <button
          onClick={() => setShowRevokeConfirm(true)}
          className="w-full text-red-400 font-semibold text-[12px] py-2.5 mt-1"
        >
          {t('share.revokeAccess')}
        </button>
      </div>

      <ConfirmModal
        open={showRevokeConfirm}
        title={t('share.revokeConfirmTitle')}
        message={t('share.revokeConfirmMessage')}
        confirmLabel={t('share.revokeAccess')}
        cancelLabel={t('common.cancel')}
        confirmColor="#EF4444"
        loading={revokingAccess}
        icon="warning"
        onConfirm={handleRevokeAccess}
        onCancel={() => { if (!revokingAccess) setShowRevokeConfirm(false) }}
      />

      {/* The Fax modal and the PIN modal both stood here, and both are gone - see the note
          on the "more options" row above. Neither could have survived this merge in any
          case: the state each one reads was deleted by the OTHER branch. The fax block
          reads setShowFaxModal, faxNumber and formatFaxNumber; the PIN block reads
          pinDigits, handlePinDigitChange and handleSetPin. After this merge not one of
          those six is declared in this file. */}

    {/* Doctor Profile Bottom Sheet — #4 */}
    {showDoctorProfile && createPortal(
      <div className="fixed inset-0 z-[9998] flex items-end justify-center" onClick={() => setShowDoctorProfile(null)}>
        <div className="absolute inset-0 bg-black/40" style={{ animation: 'fadeIn 0.2s ease' }} />
        <div
          className="relative w-full max-w-lg bg-white rounded-t-3xl max-h-[85vh] overflow-auto pb-8 bottom-actions [--ba-min:32px]"
          style={{ animation: 'slideUp 0.3s ease' }}
          onClick={(e) => e.stopPropagation()}
        >
          <style>{`
            @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
          `}</style>

          {/* Drag handle */}
          <div className="sticky top-0 z-10 bg-white rounded-t-3xl pt-3 pb-2 flex justify-center">
            <div className="w-10 h-1 rounded-full bg-gray-300" />
          </div>

          <div className="px-5">
            {/* Photo + name */}
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-full bg-[#E9F8FF] border-2 border-[#A7D9FC] flex items-center justify-center flex-shrink-0 overflow-hidden">
                {showDoctorProfile.profile_photo ? (
                  <img src={showDoctorProfile.profile_photo} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[#46A8F9] font-bold text-lg">
                    {showDoctorProfile.first_name?.[0]}{showDoctorProfile.last_name?.[0]}
                  </span>
                )}
              </div>
              <div>
                <h2 className="text-lg font-bold" style={{ color: '#15354F' }}>
                  Dr. {showDoctorProfile.first_name} {showDoctorProfile.last_name}
                  {showDoctorProfile.credentials ? `, ${typeof showDoctorProfile.credentials === 'string' ? showDoctorProfile.credentials : showDoctorProfile.credentials?.text || ''}` : ''}
                </h2>
                <p className="text-[#46A8F9] font-medium text-sm">{formatProviderSpecialty(showDoctorProfile.specialty, t)}</p>
                {showDoctorProfile.board_certified && (
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded mt-1">
                    <Shield size={10} /> {t('share.boardCertified')}
                  </span>
                )}
              </div>
            </div>

            {/* Bio */}
            {showDoctorProfile.bio && (
              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: '#94A3B8' }}>{t('share.about')}</p>
                <p className="text-[13px] text-gray-600 leading-relaxed">{showDoctorProfile.bio}</p>
              </div>
            )}

            {/* Practice info */}
            {(showDoctorProfile.practice_name || showDoctorProfile.practice_city) && (
              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: '#94A3B8' }}>{t('share.practice')}</p>
                <div className="space-y-1.5">
                  {showDoctorProfile.practice_name && (
                    <div className="flex items-center gap-2 text-[13px] text-gray-700">
                      <Building size={13} className="text-[#46A8F9] flex-shrink-0" />
                      <span className="font-medium">{showDoctorProfile.practice_name}</span>
                    </div>
                  )}
                  {(showDoctorProfile.practice_address || showDoctorProfile.practice_city) && (
                    <div className="flex items-start gap-2 text-[13px] text-gray-500">
                      <MapPin size={13} className="text-[#46A8F9] flex-shrink-0 mt-0.5" />
                      <span>
                        {/* v222.4.8 — defensive: practice_address may be JSONB dict / JSON-string */}
                        {showDoctorProfile.practice_address && `${formatAddress(showDoctorProfile.practice_address)}, `}
                        {showDoctorProfile.practice_city}
                        {showDoctorProfile.practice_state && `, ${showDoctorProfile.practice_state}`}
                        {showDoctorProfile.practice_zip && ` ${showDoctorProfile.practice_zip}`}
                      </span>
                    </div>
                  )}
                  {(showDoctorProfile.practice_phone || showDoctorProfile.phone) && (
                    <div className="flex items-center gap-2 text-[13px] text-gray-500">
                      <Phone size={13} className="text-[#46A8F9] flex-shrink-0" />
                      <span>{showDoctorProfile.practice_phone || showDoctorProfile.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Google rating */}
            {showDoctorProfile.google_rating && (
              <div className="mb-4">
                <div className="flex items-center gap-1.5 text-[13px]">
                  <Star size={14} className="fill-amber-400 text-amber-400" />
                  <span className="font-semibold" style={{ color: '#15354F' }}>{showDoctorProfile.google_rating}</span>
                  {showDoctorProfile.google_review_count && (
                    <span className="text-gray-400">({showDoctorProfile.google_review_count} reviews)</span>
                  )}
                </div>
              </div>
            )}

            {/* Languages */}
            {(showDoctorProfile.languages_spoken || showDoctorProfile.languages || []).length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: '#94A3B8' }}>{t('share.languages')}</p>
                <div className="flex flex-wrap gap-1.5">
                  {(showDoctorProfile.languages_spoken || showDoctorProfile.languages || []).map((lang, i) => (
                    <span key={i} className="text-xs font-medium px-3 py-1 rounded-full" style={{ color: '#15354F', background: '#E9F8FF' }}>
                      {typeof lang === 'string' ? lang : lang.name || lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {(showDoctorProfile.education || []).length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: '#94A3B8' }}>{t('share.education')}</p>
                <div className="space-y-1.5">
                  {showDoctorProfile.education.map((edu, i) => (
                    <div key={i} className="flex items-center gap-2 text-[13px] text-gray-600">
                      <GraduationCap size={13} className="text-[#46A8F9] flex-shrink-0" />
                      <span>{typeof edu === 'string' ? edu : edu.school || edu.name || JSON.stringify(edu)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Full profile link */}
            <button
              onClick={() => {
                const docId = showDoctorProfile.id
                setShowDoctorProfile(null)
                setFullProfileSheet({ doctorId: docId, mode: 'select' })
              }}
              className="w-full py-3 rounded-xl border-[1.5px] border-[#A7D9FC] text-[#46A8F9] font-semibold text-sm flex items-center justify-center gap-2 active:bg-[#E9F8FF] transition-colors mb-2"
            >
              {t('scheduleFollowup.viewFullProfile')}
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}

    <AgreementPreviewModal isOpen={showAgreementPreview} onClose={() => setShowAgreementPreview(false)} signatureData={agreementSigned ? (signature || '') : ''} doctor={selectedDoctor} />

    {/* v217.1.7: Doctor profile sheet — shared helper, see definition above. */}
    {renderProfileSheet()}

    </div>
  )
}

// Helper to calculate profile completion status

